import { ActionsDropdown, DropdownMenu, useToast, SidebarToggle, matchesExactKey, createPlatformShortcut, Icon, AccordionContent, AccordionTrigger, AccordionItem, Accordion } from "@aidkitorg/component-library";
import { PublishProgressModal } from "./PublishProgressModal";
import { usePublishProgress } from "./usePublishProgress";
import { QuickJS } from "@aidkitorg/types/lib/quickJS";
import { enableKeyIsValid } from "@aidkitorg/types/lib/eval";
import { ApplicantInfoMatchLookup, Computed, DefinitionComponent, ExpandedSurvey, ForceSourceMap, NavigationReferenceNode, Root, Subsurvey, Survey, TargetFieldRef, TemplatedBlock, Text } from "@aidkitorg/types/lib/survey";
import { CollectScopeForUser, EmailToUserId, NavigationNode } from "@aidkitorg/types/lib/translation/permissions";
import { DependencyMap, DependencyNode } from "@aidkitorg/types/lib/translation/questions";
import { expandTemplatesStrict, extractDefinitions } from "@aidkitorg/types/lib/translation/v0_to_legacy";
import { resolveTemplatedBlockIterations } from "@aidkitorg/types/lib/translation/templates";
import { ChangeSet, reconstructStateFromEvents, reduceEvents, State, UserActivity } from "@aidkitorg/typesheets/lib/collab";
import React, { MouseEvent, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { get_deployment, usePost, useRoboScreener } from "./API";
import { useCollabActions, CollabBusyLabel } from "./CollabWrapper";
import { Dropdown } from "./Components/Dropdown";
import RoboNavConsole from "./Components/RoboNavConsole";
import AutomaticMessageHandler from "./Config/AMH";
import { Changes } from "./Config/Changes";
import { FloatingAIChat, type AIChatOpenRequest, type EditSafety } from "./Config/FloatingAIChat";
import { buildAskAIDraft } from "./Config/askAIContext";
import type { TransactionApplier } from "./Config/editConfigTool";
import { NotificationsView } from "./Config/Notifications";
import PaymentsEditor from "./Config/Payments";
import SurveyPreview from "./Config/SurveyPreview";
import ApplicantPortalPreview from "./Config/ApplicantPortalPreview";
import TestCases from "./Config/TestCases";
import enumerateSubsurveys from "./Config/Util";
import ConfigSearch, { buildFieldMismatchPathMap, FieldMismatchMap, findAndGoToComponent, highlightSearchTerm } from "./ConfigSearch";
import { ContentTemplateModal } from "./ContentTemplateModal";
import InterfaceContext, { ConfigurationContext, PublicConfigurationContext, ScrollToTopContext, UserInfoContext } from "./Context";
import { ConfigDashboardsPreview } from "./AdminPanel/Dashboards/components/ConfigDashboardsPreview";
import { StartReviewConflictModal } from "./Config/StartReviewConflictModal";
import { BranchDropdown } from "./Config/BranchDropdown";
import { MergeBranchModal } from "./Config/MergeBranchModal";
import { PublishedChangesModal } from "./Config/PublishedChangesModal";
import { supportedLanguages, useLocalizedStrings } from "./Localization";
import { MIGRATIONS } from "./Migrations";
import { InfoDict } from "./Questions/Props";
import { FacePile } from "./Realtime";
import { RoboscreenerSurveyErrorsModal } from "./RoboscreenerSurveyErrorsModal";
import { TranslationModal, TopLevelItem } from "./TranslationModal";
import { ProgramPhaseIndicator } from "./Config/ProgramPhaseIndicator";

// Lazy load TargetFieldExplorer to defer loading of cytoscape and cytoscape-elk
const TargetFieldExplorer = React.lazy(() => import("./TargetFieldExplorer").then(m => ({ default: m.TargetFieldExplorer })));
import { forceInitiateFileDownload, generateRandomString, getDistroBrowserIncompatibility, safeParse, stringToColor, SpacedSpinner, useInterval, DISTRO_DEFAULT_LIGHTNESS } from "./Util";
import { getDependencyMap, findAnyDownstreamNotifications } from "@aidkitorg/types/lib/translation/dependencyMap";
import type { ReviewRequiredResult } from "@aidkitorg/aidkit/lib/distro/reviewRequired";
import { NON_COLLAB_PUBLISH_STEPS, NON_COLLAB_ENTIRE_PROGRAM_PUBLISH_STEPS, COLLAB_PUBLISH_STEPS, COLLAB_ENTIRE_PROGRAM_PUBLISH_STEPS } from '@aidkitorg/types/lib/distroFileNames';
import { ReviewRequiredModal } from "./Config/ReviewRequiredModal";
import { ResetProgramModal } from "./ResetProgramModal";
import { DistroValidation, DistroValidationModal } from "./Config/DistroValidation";
import { validateDistro, ValidationResult } from "@aidkitorg/types/lib/distroValidator/distroValidator";
import { loadRemoteSurvey } from "./collabRemote";
import { captureException } from "@sentry/react";
import { SurveyName } from "@aidkitorg/types/lib/distroFileNames";
import { CustomUIRenderers, useCustomUI } from "@aidkitorg/typesheets/lib/useCustomUi";
import { TemplatedBlockPreview } from "./configCustomUi/TemplatePreview";
import { NavigationPreview, buildNavTargetIndex } from "./Config/NavigationPane";
import { useTrackEvent } from "./Hooks/TrackEvent";
import { deepEqual } from "@aidkitorg/types/lib/utils/jsonDiff";

// Definitions have two on-disk shapes (see WithLegacy<DefinitionComponentWithKind,
// DefinitionComponentWithoutKind> in survey.ts): the current shape carries `kind: 'Definition'`
// and a `label` directly; the legacy shape carries the wrapped component's own `kind` at the top
// level plus a `definitionProps: { kind: 'Definition', label }` side-object. Same discrimination
// `extractDefinitions` (v0_to_legacy.ts) uses for `id`, applied here to `label` instead.
function getDefinitionLabel(node: any): string | undefined {
  if (!node || typeof node !== 'object') return undefined;
  if (node.kind === 'Definition') return node.label;
  if (node.definitionProps?.kind === 'Definition') return node.definitionProps.label;
  return undefined;
}

function getTopLevelItems(root: any): TopLevelItem[] {
  const components: any[] = Array.isArray(root) ? root : root?.survey ?? [];
  const componentItems = components
    .filter((c: any) => ['Collection', 'Section', 'Subsurvey', 'Notification'].includes(c.kind))
    .map((c: any) => {
      let label: string;
      if (c.kind === 'Collection') label = c.name?.en ?? 'Unnamed Collection';
      else if (c.kind === 'Section') label = c.title?.en ?? 'Unnamed Section';
      else if (c.kind === 'Subsurvey') label = c.title?.en ?? c.path ?? 'Unnamed Subsurvey';
      else label = c.name ?? 'Unnamed Notification';
      return { label, root: c, kind: c.kind };
    });
  // Definitions live in the sibling `survey.definitions` array (only canonically populated on
  // the entireprogram document — see survey.ts's Definitions field comment), not in `survey.survey`,
  // so they need their own enumeration. Only top-level entries become selectable items, same as
  // Collection/Section/Subsurvey/Notification above; a selected Definition Bucket's nested
  // Definitions (arbitrarily deep, per extractDefinitions in v0_to_legacy.ts) are picked up when
  // its subtree is walked by extractDedupedTranslations, not flattened into separate items here.
  const definitions: any[] = Array.isArray(root) ? [] : root?.definitions ?? [];
  const definitionItems = definitions.map((d: any) => {
    if (d.kind === 'Definition Bucket') {
      return { label: d.name?.en ?? 'Unnamed Definition Bucket', root: d, kind: 'Definition Bucket' };
    }
    return { label: getDefinitionLabel(d) ?? 'Unnamed Definition', root: d, kind: 'Definition' };
  });
  return [...componentItems, ...definitionItems];
}

function extractDedupedTranslations(root: any, stack: Set<string> = new Set(), includeHidden = false, definitionLabel?: string): { en: string, [key: string]: string }[] {
  if (typeof root.en === "string") {
    if (!root._id || stack.has(root._id)) {
      root._id = uuidv4();
    }
    stack.add(root._id);
    const tagged = { ...root };
    // Tag with the label of the nearest enclosing Definition (if any) so the modal can show
    // translators what a string belongs to. The label itself is a plain string (DefinitionLabel),
    // never a Text/locale object, so it's never picked up as a translatable string on its own.
    if (definitionLabel) tagged._definitionLabel = definitionLabel;
    return [tagged];
  }
  if (Array.isArray(root)) {
    return root.flatMap(i => extractDedupedTranslations(i, stack, includeHidden, definitionLabel));
  }
  if (typeof root === "object") {
    if (!includeHidden && root.hidden === true) return [];
    const nextDefinitionLabel = getDefinitionLabel(root) ?? definitionLabel;
    return Object.values(root).flatMap(i => extractDedupedTranslations(i, stack, includeHidden, nextDefinitionLabel));
  }
  return [];
}

function importTranslations(root: any, strings: { _id?: string, [key: string]: string | undefined }[]) {
  if (typeof root.en === "string" && root._id) {
    let translation = strings.find((s) => s._id === root._id);
    if (translation) {
      Object.keys(translation).forEach((key) => {
        if (key !== '_id' && key !== 'kind' && key !== '_definitionLabel' && translation![key]) {
          root[key] = translation![key];
        }
      });
    }
  }
  if (Array.isArray(root)) {
    root.flatMap((r) => importTranslations(r, strings));
  }
  if (typeof root === "object") {
    Object.values(root).flatMap((r) => importTranslations(r, strings));
  }
}

export const DistroContext = React.createContext<DependencyMap>(new Map<TargetFieldRef, DependencyNode>());

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

type PreviewTab = 'survey' | 'notifications' | 'payments' | 'targetFields' | 'dashboards' | 'testCases' | 'changes' | 'amh' | 'fieldMismatches' | 'robonav' | 'validationSummary' | 'navigation' | 'applicantPortal';

/** Defaults differ by survey kind, and each kind remembers its own tab bar. */
type PreviewTabScope = 'adminPanel' | 'survey';

/**
 * Tabs shown before any customization - AP survey gets a lot fewer tabs than entireprogram / non-AP surveys
 */
const DEFAULT_PREVIEW_TABS: Record<PreviewTabScope, PreviewTab[]> = {
  adminPanel: ['changes'],
  survey: ['changes', 'survey', 'notifications', 'payments', 'navigation'],
};
/** Changes is the reason the panel exists, so it is the one tab that cannot be removed. */
const PERMANENT_PREVIEW_TAB: PreviewTab = 'changes';
const PREVIEW_TABS_STORAGE_KEY = 'distroPreviewTabs';

/**
 * entireprogram and adminpanel are the always-present default surveys: landing on either opens
 * both, and neither can be closed. These are default surveys that all (new) programs should have.
 */
const DEFAULT_SURVEYS: string[] = [SurveyName.ENTIRE_PROGRAM, SurveyName.ADMIN_PANEL];
const isDefaultSurvey = (name: string) => DEFAULT_SURVEYS.includes(name);


export default function ConfigPage(props?: { macroEditor?: boolean }) {
  const params = useParams<{ survey: string }>();
  const history = useHistory();
  const urlConfigName = params.survey || window.location.hash.slice(1);

  // Tabs shown in the strip. Defaults are always present; a non-default survey opens alongside them.
  const [openTabs, setOpenTabs] = useState<string[]>(
    isDefaultSurvey(urlConfigName) ? [...DEFAULT_SURVEYS] : [...DEFAULT_SURVEYS, urlConfigName]
  );
  const [activeTab, setActiveTab] = useState(urlConfigName);
  // Which tabs are actually mounted (loaded). Lazy: only the ones that have been visited.
  const [mountedTabs, setMountedTabs] = useState<string[]>([urlConfigName]);
  // Mirrors so closeTab can pick the neighbouring tab synchronously.
  const openTabsRef = useRef(openTabs);
  openTabsRef.current = openTabs;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  // Keep the URL in sync with the visible tab (shareable/bookmarkable). replace, not push, so
  // rapid tab switching doesn't flood the back stack. Skipped in the single-survey macro editor.
  const syncUrl = useCallback((name: string) => {
    if (props?.macroEditor || !name) return;
    history.replace('/config/' + name);
  }, [history, props?.macroEditor]);

  const selectTab = useCallback((name: string) => {
    setActiveTab(name);
    setMountedTabs((prev) => (prev.includes(name) ? prev : [...prev, name]));
    syncUrl(name);
  }, [syncUrl]);

  const addTab = useCallback((name: string) => {
    setOpenTabs((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setMountedTabs((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setActiveTab(name);
    syncUrl(name);
  }, [syncUrl]);

  const closeTab = useCallback((name: string) => {
    const prev = openTabsRef.current;
    // Defaults are permanent, and never close the last remaining tab.
    if (isDefaultSurvey(name) || prev.length <= 1) return;
    const idx = prev.indexOf(name);
    const next = prev.filter((t) => t !== name);
    const wasActive = activeTabRef.current === name;
    // If the closed tab was active, fall back to its left neighbour (Chrome-style).
    const fallback = next[Math.max(0, idx - 1)];
    setOpenTabs(next);
    setActiveTab((cur) => (cur === name ? fallback : cur));
    setMountedTabs((m) => {
      const kept = m.filter((t) => t !== name);
      // Mounting is lazy, so the fallback may never have been visited — mount it here. Only
      // mounted tabs render, so otherwise activating an unmounted fallback renders nothing at
      // all (e.g. close a survey tab when adminpanel has never been opened -> blank page).
      if (!wasActive || kept.includes(fallback)) return kept;
      return [...kept, fallback];
    });
    if (wasActive) syncUrl(fallback);
  }, [syncUrl]);

  // Bumping a survey's nonce changes its editor's key, remounting just that instance — a page
  // reload scoped to one tab. Used when another user toggles collab on that survey, where a
  // location.reload() would also destroy sibling tabs' unsaved in-memory edits.
  const [reloadNonces, setReloadNonces] = useState<Record<string, number>>({});
  const reloadEditor = useCallback((name: string) => {
    setReloadNonces((prev) => ({ ...prev, [name]: (prev[name] || 0) + 1 }));
  }, []);

  // The macro editor is single-survey; no tab strip.
  if (props?.macroEditor) {
    return <ConfigEditor
      key={reloadNonces[urlConfigName] || 0}
      configName={urlConfigName}
      macroEditor
      isActive
      onRemoteCollabToggle={() => reloadEditor(urlConfigName)}
    />;
  }

  return (
    <>
      {openTabs.filter((name) => mountedTabs.includes(name)).map((name) => (
        // display:contents so the active tab lays out exactly as a lone ConfigPage would,
        // while inactive tabs take no layout space yet keep their in-memory edits alive
        // (their collab socket is paused while hidden; they resync on reactivation).
        <div key={name} style={{ display: name === activeTab ? 'contents' : 'none' }}>
          <ConfigEditor
            key={reloadNonces[name] || 0}
            configName={name}
            isActive={name === activeTab}
            tabs={openTabs}
            activeTab={activeTab}
            onSelectTab={selectTab}
            onAddTab={addTab}
            onCloseTab={closeTab}
            onRemoteCollabToggle={() => reloadEditor(name)}
          />
        </div>
      ))}
    </>
  );
}

type SurveyTabsProps = {
  /** All surveys open as tabs. When present, a Chrome-style tab strip renders above the top bar. */
  tabs?: string[];
  /** Which tab is currently visible, used to highlight the active tab. */
  activeTab?: string;
  /** Switch the visible tab. Does NOT change the URL. */
  onSelectTab?: (name: string) => void;
  /** Open another survey as a new tab. */
  onAddTab?: (name: string) => void;
  /** Close a tab (never the last remaining one). */
  onCloseTab?: (name: string) => void;
};

/**
 * A single Distro editor instance for one survey. All per-survey state (collab, publish,
 * previews) hangs off `configName`. The tab props are supplied by the tabbed ConfigPage
 * wrapper so each editor can render the shared tab strip above its own chrome.
 */
function ConfigEditor(props: {
  macroEditor?: boolean;
  /** Survey this instance loads. Drives collab, publish, and all per-survey state. */
  configName: string;
  /** False when this instance is mounted-but-hidden behind another tab. */
  isActive?: boolean;
  /** Remote collab enable/disable for this survey: remount just this editor instead of
   *  location.reload(), so sibling tabs' in-memory edits survive. */
  onRemoteCollabToggle?: () => void;
} & SurveyTabsProps) {
  const isActiveInstance = props.isActive !== false;
  const [survey, setSurvey] = useState<ExpandedSurvey | null>(null);

  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);
  const stateRef = useRef<State | undefined>(undefined);
  const surveyRef = useRef<ExpandedSurvey | null>(null);
  /**
   * The survey clone `extractDedupedTranslations` last assigned `_id`s onto.
   * `injestTranslations` matches by those ids, so it must import onto this copy
   * (not Distro's live `read()` tree, which never received ids assigned during
   * a scope change).
   */
  const translationExtractRef = useRef<ExpandedSurvey | null>(null);
  /** Queue of events that arrived before distroRef is ready to merge them */
  const pendingEventsQueue = useRef<Array<{
    events: ChangeSet,
    userActivity?: UserActivity[],
    unpublishedEvents?: ChangeSet
  }>>([]);

  const L = useLocalizedStrings();
  const history = useHistory();

  const user = useContext(UserInfoContext);
  const publicConfiguration = useContext(PublicConfigurationContext);
  const [tabId, setTabId] = useState(generateRandomString(4, 'abcdefghjkmnpqrstuvwxyz123456789'));
  const { toast } = useToast();

  const [mockInfo, setMockInfo] = useState<InfoDict>({});

  const [distroConfigName, setDistroConfigName] = useState(props.configName);
  const [loadedVersionId, setLoadedVersionId] = useState("");
  const [latestVersionId, setLatestVersionId] = useState("");
  /** Whether the published survey has changed since loaded version, or the base-commit if applicable */
  const surveyChangedSinceLoaded = useMemo(() => {
    return !!(loadedVersionId && latestVersionId && loadedVersionId !== latestVersionId);
  }, [loadedVersionId, latestVersionId]);
  /** Whether the survey has changed since the latest active survey */
  const [showBreadcrumb, setShowBreadcrumb] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [translations, setTranslations] = useState<Text[]>([]);
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [contentTemplateModalOpen, setContentTemplateModalOpen] = useState(false);
  const [startReviewConflictModalOpen, setStartReviewConflictModalOpen] = useState(false);
  const initialTranslations = useRef('');
  const interfaceContext = useContext(InterfaceContext);

  const [surveys, setSurveys] = useState<Awaited<ReturnType<typeof listSurveys>> | null>(null);
  const loadingSurveys = useRef(false);
  const [surveyName, setSurveyName] = useState("");
  const [activeSurvey, setActiveSurvey] = useState<string | null | undefined>((isActiveInstance ? window.location.hash.slice(1) : '') || undefined);
  const [commitId, setCommitId] = useState<string | null>(new URLSearchParams(window.location.search).get('commit') || null);
  const [latestCommitMetadata, setLatestCommitMetadata] = useState<{ id: string, reviewId: string, reviewStatus: string } | null>(null);
  
  const [selectedPreview, setSelectedPreview] = useState<PreviewTab>(PERMANENT_PREVIEW_TAB);
  // Tab bars the user has customized, keyed by survey kind. A missing scope means "still on the defaults".
  const [customPreviewTabs, setCustomPreviewTabs] = useState<Partial<Record<PreviewTabScope, PreviewTab[]>>>({});
  const trackEvent = useTrackEvent();

  const previewTabScope: PreviewTabScope = distroConfigName === SurveyName.ADMIN_PANEL ? 'adminPanel' : 'survey';
  const visiblePreviewTabKeys = customPreviewTabs[previewTabScope] ?? DEFAULT_PREVIEW_TABS[previewTabScope];

  const updatePreviewTabsForScope = useCallback((update: (tabs: PreviewTab[]) => PreviewTab[]) => {
    setCustomPreviewTabs((custom) => ({
      ...custom,
      [previewTabScope]: update(custom[previewTabScope] ?? DEFAULT_PREVIEW_TABS[previewTabScope]),
    }));
  }, [previewTabScope]);

  /**
   * Select a tab, adding it to the bar first if it is currently in the overflow menu. New tabs go on
   * the end so the bar reads in the order the user built it.
   */
  const selectPreviewTab = useCallback((tab: PreviewTab, source: 'tab_bar' | 'overflow_menu' | 'validation_link') => {
    const wasInTabBar = visiblePreviewTabKeys.includes(tab);
    updatePreviewTabsForScope((tabs) => tabs.includes(tab) ? tabs : [...tabs, tab]);
    setSelectedPreview(tab);
    // Which panes actually get opened, so the unused ones can be retired.
    trackEvent('distro_preview_tab_selected', {
      tab,
      source,
      surveyKind: previewTabScope,
      isDefaultSurvey: DEFAULT_PREVIEW_TABS[previewTabScope].includes(tab)
    });
  }, [visiblePreviewTabKeys, updatePreviewTabsForScope, trackEvent, previewTabScope]);

  const removePreviewTab = useCallback((tab: PreviewTab) => {
    if (tab === PERMANENT_PREVIEW_TAB) return;
    updatePreviewTabsForScope((tabs) => tabs.filter((visibleTab) => visibleTab !== tab));
    // Don't leave the pane showing a tab that just disappeared.
    setSelectedPreview((selected) => selected === tab ? PERMANENT_PREVIEW_TAB : selected);
    // A tab someone bothers to remove is the strongest deprecation signal available.
    trackEvent('distro_preview_tab_removed', { tab, surveyKind: previewTabScope });
  }, [updatePreviewTabsForScope, trackEvent, previewTabScope]);
  const config = useContext(ConfigurationContext);
  const [subsurveys, setSubsurveys] = useState(null as null | Subsurvey[]);
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiChatHeight, setAiChatHeight] = useState(0);
  const [aiChatOpenRequest, setAIChatOpenRequest] = useState<AIChatOpenRequest | null>(null);

  const openAIChatForProperty = useCallback((resolveContext: AIChatOpenRequest['resolveContext']) => {
    const state = stateRef.current;
    if (!state) return;
    const context = resolveContext();
    if (!context) return;
    setAIChatOpenRequest({
      draft: buildAskAIDraft(context),
      // A reinitialized editor/branch must not resolve the selection against a different document.
      resolveContext: () => stateRef.current === state ? resolveContext() : null,
    });
    setShowAIChat(true);
  }, []);

  const closeAIChat = useCallback(() => {
    setShowAIChat(false);
    setAiChatHeight(0);
    setAIChatOpenRequest(null);
  }, []);

  const [roboscreenerValidationErrors, setRoboscreenerValidationErrors] = useState<(string | { hint: string, error: string })[]>();
  const [distroValidationErrorsForModal, setDistroValidationErrorsForModal] = useState<ValidationResult[] | null>(null);
  const [reviewRequired, setReviewRequired] = useState<ReviewRequiredResult | null>(null);

  const loadSurvey = usePost('/survey/load_survey');
  const loadSurveyMetadata = usePost('/survey/load_survey_metadata');
  const rebaseNonCollabPost = usePost('/survey/rebase_non_collab', { compressRequestPayload: true });
  const saveSurvey = usePost('/survey/save_survey', { compressRequestPayload: true });
  const validateSurvey = usePost('/survey/validate_survey', { compressRequestPayload: true });
  const doMagic = usePost('/program/admin/magicV2');
  const doesSurveyExist = usePost('/survey/does_survey_exist');
  const sendNotifications = usePost("/program/admin/force_notifications_immediately");
  const validateSurveyRS = useRoboScreener("/check_survey_validity", { compressRequestPayload: true });

  const getEverything = usePost('/applicant/get_everything');
  const listSurveys = usePost('/survey/list_surveys');
  const startDistroReviewReq = usePost('/distro-review/start', { compressRequestPayload: true });
  const getDistroCommit = usePost('/distro-review/commit/get');
  const getLatestDistroCommitMetadata = usePost('/distro-review/commit/get-latest', { suppressErrorDisplay: true });
  const getMacroRequest = usePost('/survey/get_macro');

  const saveCollabDynamoEvents = usePost('/survey/save_collab_dynamo_events');

  // Raw posts for AI cross-survey reads — the useCollabActions handles are bound
  // to the OPEN survey/branch, and these must target a sibling document.
  const getRemoteCollabEnabled = usePost('/survey/get_collab_enabled');
  const getRemoteBranches = usePost('/survey/get_branches');
  const getRemoteCollabEvents = usePost('/survey/get_collab_events');

  useEffect(() => {
    // Only the visible editor owns the URL hash. Two coupled editors (entireprogram +
    // adminpanel) are mounted at once; without this guard the hidden one would clobber
    // the active one's subsurvey hash.
    if (!isActiveInstance) return;
    window.location.hash = activeSurvey ? '#' + activeSurvey : '';
  }, [activeSurvey, isActiveInstance]);

  async function getMacro(macroConfig: any): Promise<any> {
    const getMacroResult = await getMacroRequest({ macroConfig });
    const macroGenerator = safeParse(getMacroResult.macro!).survey
    async function evaluateIncludeIf(condition: string, macroConfig: any): Promise<boolean> {
      const result = await QuickJS.compute(condition, { macroConfig });
      return result instanceof Error ? false : result;
    }

    async function applyUpdate(component: any, updateCode: string, macroConfig: any): Promise<any> {
      const updatedComponent = await QuickJS.compute(updateCode, { component, macroConfig });
      if (updatedComponent instanceof Error) {
        captureException(new Error("Error applying update from macro"), { extra: { updateCode, component, macroConfig } });
        return '';
      }
      return updatedComponent;
    }

    async function traverseAndExpand(data: any, macroConfig: any): Promise<any> {
      // TODO(fay): handle nested Macros so we can combine them into a single parent macro
      if (Array.isArray(data)) {
        const updatedComponents = [];

        for (const component of data) {
          let updatedComponent = component;

          if (component.macroIncludeIf) {
            if (!(await evaluateIncludeIf(component.macroIncludeIf, macroConfig))) {
              continue;
            }
            delete updatedComponent.macroIncludeIf;
          }

          if (component.macroUpdate) {
            updatedComponent = await applyUpdate(updatedComponent, component.macroUpdate, macroConfig);
            delete updatedComponent.macroUpdate;
          }

          if (updatedComponent && typeof updatedComponent === 'object') {
            updatedComponent = await traverseAndExpand(updatedComponent, macroConfig);
          }

          if (updatedComponent) updatedComponents.push(updatedComponent);
        }

        return updatedComponents;

      } else if (data && typeof data === 'object') {
        const updatedData: any = {};

        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            updatedData[key] = await traverseAndExpand(data[key], macroConfig);
          }
        }

        return updatedData;
      }

      return data;
    }

    return traverseAndExpand(macroGenerator, macroConfig.type);
  }

  // Capture branch name from URL before useCollabActions so it can initialize to the right branch
  const initialBranchParam = useRef(Number(new URLSearchParams(window.location.search).get('branch')) || undefined);

  const handleUserRemoved = useCallback((uid: string, tabId: string) => {
    if (stateRef.current && distroRef.current) {
      distroRef.current.handleUserRemoved(stateRef.current, uid, tabId);
    }
  }, []);

  // State management & actions for Collab Mode
  const {
    // if collab is not enables, this hook doesn't do much
    collabEnabled,
    // true once the collab check has completed (regardless of collab being on/off)
    collabReady,
    // which distro branch we're on
    branchId,
    branchName,
    // false if collab is actively receiving and merging events, otherwise true
    canPublish,
    // which channel collab is listening to
    channel,
    // tell listening clients that you just published
    alertJustPublished,
    // refresh collab events and settings
    getCollabState,
    // to be called whenever you make a distro change - this handles sending events in realtime 
    // and also persisting new events to dynamo / s3
    handleNewCollabEvents,
    // enables collab from raw survey JSON, sends websocket event to notify other clients.
    enableCollab,
    // updates state var and persisted collab settings.
    // also triggers a refresh on other clients listening browsers.
    disableCollab,
    // uploads large amounts of events and saves a new version, with those events
    // merged into the exising ops.
    writeNewCollabVersion,
    // branch management
    branches,
    createBranch,
    switchBranch,
    collabBusy,
    rebaseBranch,
    setContextOperationInProgress,
    deleteBranch,
    resetBranchToPublished,
    behindPublished,
    parentStateRef,
    parentOpsRef,
    fetchPublishedChanges,
  } = useCollabActions({
    surveyName: distroConfigName,
    tabId,
    initialBranchId: initialBranchParam.current,
    mergeReceivedEventsCallback: (events: ChangeSet, userActivity?: UserActivity[], initialize?: boolean, debug?: boolean, unpublishedEvents?: ChangeSet, collabEnabled?: boolean) => {
      if (initialize) {
        distroRef.current?.initialize(survey,
          { ...user, name: config?.user?.name, tabId },
          events,
          userActivity,
          undefined,
          undefined,
          unpublishedEvents,
          // collabEnabled arms the editor's guard against silently rebuilding from the
          // stale raw `survey` prop when collab is on but events came back empty — edits
          // made after such a rebuild reference fresh object ids and can never attach
          // server-side.
          { enableKeyIsValid, debug, collabEnabled }
        );
        
        // Flush queued events that arrived before initialization
        pendingEventsQueue.current.forEach(({events, userActivity, unpublishedEvents}) => {
          distroRef.current?.mergeEvents(events, userActivity, unpublishedEvents);
        });
        pendingEventsQueue.current = [];
        
      } else if (!distroRef.current) {
        // Queue events received before distroRef is ready
        pendingEventsQueue.current.push({events, userActivity, unpublishedEvents});
      } else {
        distroRef.current.mergeEvents(events, userActivity, unpublishedEvents);
      }
    },
    autosave: true,
    source: 'distro',
    // A mounted-but-hidden tab pauses its collab socket + background polling; it resyncs on
    // reactivation (see the activation effect below).
    paused: !isActiveInstance,
    onRemoteCollabToggle: props.onRemoteCollabToggle,
    skipSelfEchoedEvents: true,
  });

  const publishProgress = usePublishProgress();
  const [publishing, setPublishing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [togglingCollab, setTogglingCollab] = useState(false);
  const [pendingMergeResult, setPendingMergeResult] = useState<any & { isRebase?: boolean, publishAfterResolve?: boolean }>(undefined);
  useEffect(() => {
    setContextOperationInProgress(publishing || !!pendingMergeResult);
  }, [publishing, pendingMergeResult, setContextOperationInProgress]);
  const mergeBaseState = useMemo(() =>
    pendingMergeResult?.baseOps ? reconstructStateFromEvents([...(parentOpsRef?.current ?? []), ...pendingMergeResult.baseOps])[1] : {} as State,
  [pendingMergeResult?.baseOps, parentOpsRef]
  );
  const mergeIncomingState = useMemo(() =>
    pendingMergeResult?.incomingOps ? reconstructStateFromEvents([...(parentOpsRef?.current ?? []), ...pendingMergeResult.incomingOps])[1] : {} as State,
  [pendingMergeResult?.incomingOps, parentOpsRef]
  );
  const [lastPublished, setLastPublished] = useState(null as null | string);
  const lastActivityTime = useRef(Date.now());
  const [publishedChangesModalOpen, setPublishedChangesModalOpen] = useState(false);


  // Tracks incidents of referenced target field variables in text
  // not aligning across different languages to help catch misconfigurations
  const [fieldMismatches, setFieldMismatches] = useState<FieldMismatchMap>({});

  const handleUpdatesRef = useRef<typeof handleUpdatesInner | null>(null);
  const handleUpdatesInner = (survey: ExpandedSurvey, state: State, events: ChangeSet) => {
    if (events.length && collabEnabled) {
      handleNewCollabEvents(events, state.myActivity, state);
    }

    stateRef.current = state;
    // Mirrored alongside stateRef for the same reason: callers that need the
    // post-edit survey within the same tick can't wait for setSurvey to land.
    surveyRef.current = survey;
    setSurvey(survey);
  }

  handleUpdatesRef.current = handleUpdatesInner;

  // Gross hack because distro doesn't pick up changes
  const handleUpdates = (survey: ExpandedSurvey, state: State, events: ChangeSet) => {
    if (handleUpdatesRef.current) {
      handleUpdatesRef.current(survey, state, events);
    }
  }

  /**
   * How recoverable an AI edit is here. Editing works in all three cases — the
   * editor's document is a CRDT whether or not collab is on — but only a
   * non-default branch can be deleted to throw the edits away.
   */
  const editSafety = useMemo((): EditSafety => {
    if (!collabEnabled || !branches?.length) return 'no-branches';
    const current = branches.find(b => b.id === branchId);
    // An unresolved branchId (stale URL, branch deleted under us) must not read as
    // the safe case — we can't promise there's a branch to throw away.
    if (!current) return 'unknown';
    return current.is_dev_branch ? 'default-branch' : 'branch';
  }, [collabEnabled, branches, branchId]);

  /**
   * Applies one AI-authored transaction to the live document. All ops land in one
   * changeset (one undo step, one Changes-tab entry) or nothing changes — the
   * structural/id/reference validation lives in the engine
   * (@aidkitorg/typesheets configEdit). Full Distro domain validity is
   * pull-based: the assistant runs the checkConfigValidity tool when its work is
   * done, backed by getAIValidationIssues below.
   */
  const applyAIConfigTransaction = useCallback<TransactionApplier>((ops, opts) => {
    if (!distroRef.current) return { ok: false, errors: ['The Distro editor is not loaded.'] };
    return distroRef.current.applyTransaction(ops, opts);
  }, []);

  // surveyRef, not React state: several tool calls run in one assistant response
  // before React re-renders, and a validity check must see its own turn's edits.
  // null (not []) when no survey is loaded — an empty object trips no rules, so
  // validating it would falsely report "valid". An explicit doc (a cross-survey
  // snapshot) is validated instead of the open one.
  const getAIValidationIssues = useCallback(
    (doc?: Root) => (doc ? validateDistro(doc) : surveyRef.current ? validateDistro(surveyRef.current) : null),
    []);

  // Snapshot reads of a sibling survey document (entireprogram/adminpanel/audit)
  // for the AI chat's cross-survey tool params. Caching/branch resolution live in
  // loadRemoteSurvey; errors throw with messages meant for tool results.
  const loadOtherSurvey = useCallback(
    (name: string, branch?: string) => loadRemoteSurvey(
      {
        getCollabEnabled: getRemoteCollabEnabled,
        getBranches: getRemoteBranches,
        getCollabEvents: getRemoteCollabEvents,
        loadSurvey,
      },
      { name, branchName: branch },
    ),
    [getRemoteCollabEnabled, getRemoteBranches, getRemoteCollabEvents, loadSurvey]);

  // Branch list for the chat panel's sibling-branch selector: WHICH branch of
  // the sibling document the AI reads is the user's choice, not the model's.
  // Null = the sibling has no branches (collab off) and reads as published.
  const getSiblingBranchInfo = useCallback(async () => {
    const sibling = distroConfigName === SurveyName.ENTIRE_PROGRAM ? SurveyName.ADMIN_PANEL : SurveyName.ENTIRE_PROGRAM;
    try {
      const enabled = await getRemoteCollabEnabled({ name: sibling });
      if (!enabled) return null;
      const branches = await getRemoteBranches({ survey: sibling });
      return { survey: sibling as string, branches };
    } catch {
      return null;
    }
  }, [distroConfigName, getRemoteCollabEnabled, getRemoteBranches]);

  useEffect(() => {
    if (surveys || loadingSurveys.current) return;
    loadingSurveys.current = true;
    (async () => {
      setSurveys(await listSurveys({}));
      loadingSurveys.current = false;
    })();
  }, []);

  useEffect(() => {
    const storedShowRightPanel = localStorage.getItem('showRightPanel');
    if (storedShowRightPanel) {
      setShowRightPanel(JSON.parse(storedShowRightPanel));
    }
    const storedShowBreadcrumb = localStorage.getItem('showBreadcrumb');
    if (storedShowBreadcrumb) {
      setShowBreadcrumb(JSON.parse(storedShowBreadcrumb));
    }
    const storedPreviewTabs = localStorage.getItem(PREVIEW_TABS_STORAGE_KEY);
    if (storedPreviewTabs) {
      // Keep only scopes that still look like a non-empty tab list, so a stale or hand-edited
      // value falls back to the defaults instead of rendering an empty tab bar.
      const scopes = Object.entries(safeParse(storedPreviewTabs, {}) as Record<string, unknown>)
        .filter((entry): entry is [PreviewTabScope, PreviewTab[]] =>
          entry[0] in DEFAULT_PREVIEW_TABS && Array.isArray(entry[1]) && entry[1].length > 0);
      if (scopes.length) {
        setCustomPreviewTabs(Object.fromEntries(scopes));
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('showRightPanel', JSON.stringify(showRightPanel));
  }, [showRightPanel]);

  useEffect(() => {
    localStorage.setItem('showBreadcrumb', JSON.stringify(showBreadcrumb));
  }, [showBreadcrumb]);

  useEffect(() => {
    localStorage.setItem(PREVIEW_TABS_STORAGE_KEY, JSON.stringify(customPreviewTabs));
  }, [customPreviewTabs]);

  async function initialLoad(distroConfigName?: string) {
    let distroConfigToLoad = distroConfigName;
    if (!distroConfigToLoad) {
      const enteredDistroConfigName = prompt('Enter a name to load');
      if (enteredDistroConfigName) {
        distroConfigToLoad = enteredDistroConfigName;
        setDistroConfigName(enteredDistroConfigName);
      }
    }
    if (!distroConfigToLoad) {
      return;
    }
    ['config', 'macro-editor'].forEach(path => {
      if (window.location.pathname.startsWith(`/${path}`) && !window.location.pathname.startsWith(`/${path}/`)) {
        window.location.pathname = `/${path}/${distroConfigToLoad}`;
      }
    });
    
    let surveyResp: { config: ExpandedSurvey; versionId: string; lastModified: string; references?: any };
    let survey: ExpandedSurvey;
    
    const latestCommitMetadataResp = await getLatestDistroCommitMetadata({ surveyName: distroConfigToLoad, ...(branchId ? { branchId } : {}) });
    if (latestCommitMetadataResp && !('error' in latestCommitMetadataResp)) {
      setLatestCommitMetadata(latestCommitMetadataResp);
    }
    
    if (commitId) {
      const commitSurveyResp = await getDistroCommit({ id: commitId });

      if (!commitSurveyResp || commitSurveyResp.error || !commitSurveyResp.survey) {
        toast({
          description: commitSurveyResp?.error || 'Error loading commit survey',
          variant: 'error'
        });
        return;
      }
      if (commitSurveyResp.surveyName !== distroConfigToLoad) {
        toast({
          description: 'The commit you are trying to load is for a different survey.',
          variant: 'error'
        });
        return;
      }
      
      survey = commitSurveyResp.survey;
      // For commit surveys, we don't have hash/lastModified, so set defaults
      surveyResp = {
        config: survey,
        versionId: commitSurveyResp.parentVersion || '',
        lastModified: '',
        references: undefined
      };
    } else {
      // Use regular survey endpoint
      setCommitId(null);
      surveyResp = await loadSurvey({ name: distroConfigToLoad, purpose: "load function called" });

      if (!surveyResp || 'error' in surveyResp) {
        // Survey doesn't exist yet (fresh setup) — start with an empty editor
        survey = { survey: [], personas: [] };
        surveyResp = { config: survey, versionId: '', lastModified: '' };
      } else {
        survey = surveyResp.config;
      }
    }

    // Build field-mismatch trees on the UN-EXPANDED survey so they resolve against the same
    // representation findAndGoToComponent walks (the editor renders the un-expanded survey).
    // Building on the expanded survey would desync templated-block and positional (Conditional
    // Block) paths, breaking scroll-to for those mismatches.
    if (survey?.survey) {
      setFieldMismatches(buildFieldMismatchPathMap(survey.survey));
    } else {
      setFieldMismatches({});
    }

    setLoadedVersionId(surveyResp.versionId ?? '');
    setLatestVersionId(surveyResp.versionId ?? '');
    setLastPublished(surveyResp.lastModified);

    const collabResp = await getCollabState();

    function tryLoad() {
      if (distroRef.current) {
        const userActivity = Object.keys(collabResp.userActivity || {})
          .map(k => {
            return {
              lastEditedObject: collabResp.userActivity[k].lastEditedObject,
              uid: collabResp.userActivity[k].uid,
              name: collabResp.userActivity[k].name,
              tabId: collabResp.userActivity[k].tabId
            }
          });

        distroRef.current?.initialize(survey,
          { ...user, name: config?.user?.name, tabId },
          collabResp.collabEnabled ? collabResp.events : undefined,
          collabResp.collabEnabled ? userActivity : undefined,
          surveyResp.references,
          undefined,
          collabResp.collabEnabled ? collabResp.unpublishedEvents : undefined,
          { enableKeyIsValid, debug: !!collabResp.debug, collabEnabled: collabResp.collabEnabled || false }
        );
      } else {
        setTimeout(tryLoad, 200);
      }
    }
    tryLoad();
    //setSurvey(survey);
    setSurveyName(distroConfigToLoad);
  }

  const reloadSurvey = useCallback(async (syncEvents?: boolean) => {
    if (!surveyName) return;
    // Hidden tabs stay quiet: no metadata/collab polling. They resync on reactivation.
    if (!isActiveInstance) return;

    // In collab mode, we do not need to sync persisted state as often since we are passing 
    // state back and forth via websockets. Only if we haven't had activity in the last 3 minutes 
    // should we sync to keep everything up to date.
    const currentTime = Date.now();
    const inactivityDuration = currentTime - lastActivityTime.current;
    const doSync = syncEvents || (inactivityDuration > 3 * 60 * 1000);

    const latestCommit = await getLatestDistroCommitMetadata({ surveyName, ...(branchId ? { branchId } : {}) });
    if (latestCommit && !('error' in latestCommit)) {
      setLatestCommitMetadata(prev =>
        deepEqual(prev, latestCommit) // deepEqual to cover any future changes to the shape of latestCommit
          ? prev            // unchanged → same ref → no need to re-render
          : latestCommit
      );
    }
    const metadataResponse = await loadSurveyMetadata({ name: surveyName, purpose: "reloadSurvey called" });
    if (metadataResponse && (metadataResponse.versionId !== latestVersionId || metadataResponse.lastModified !== lastPublished)) {
      setLatestVersionId(metadataResponse.versionId ?? '');
      setLastPublished(metadataResponse.lastModified);

      // causes survey to totally reload, disrupting anyone using it.
      // if (doSync) {
      //   distroRef.current?.initialize(surveyResp.config, { ...user, name: config?.user?.name, tabId });
      // }
      
    }

    if (collabEnabled) {
      if (doSync) {
        const collabResp = await getCollabState();
        distroRef.current?.mergeEvents(collabResp.events);
        lastActivityTime.current = Date.now();
      }
    }
  }, [lastActivityTime, surveyName, collabEnabled, latestVersionId, lastPublished, getCollabState, isActiveInstance]);
  useInterval(reloadSurvey, 3000);

  // When a paused (mounted-but-hidden) tab becomes active again, its socket + polling were off,
  // so catch up on anything missed while it was quiet.
  const wasActiveInstance = useRef(isActiveInstance);
  useEffect(() => {
    if (isActiveInstance && !wasActiveInstance.current) {
      reloadSurvey(true);
    }
    wasActiveInstance.current = isActiveInstance;
  }, [isActiveInstance, reloadSurvey]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      // if we are just coming back, reload to make sure we didn't miss anything.
      if (!document.hidden && collabEnabled) {
        reloadSurvey(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleActivity = () => {
      lastActivityTime.current = Date.now();
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [collabEnabled]);

  useEffect(() => {
    // Wait for the collab check to complete before loading.
    if (distroConfigName && collabReady && (!collabEnabled || branchId)) {
      initialLoad(distroConfigName);
    }
  }, [user, distroConfigName, collabReady, branchId, collabEnabled]);

  useEffect(() => {
    const previousTitle = document.title;
    const programName = publicConfiguration.name;
    document.title = programName ? `Distro | ${programName}` : 'Distro';
    return () => { document.title = previousTitle; };
  }, [publicConfiguration.name]);

  // Remove commit query parameter from URL after it's been consumed
  useEffect(() => {
    if (commitId && window.location.search.includes('commit=')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('commit');
      window.history.replaceState({}, '', url.toString());
    }
  }, [commitId]);

  // This makes the bundler include the file that is otherwise just types
  ForceSourceMap();

  useEffect(() => {
    if (!survey) return;
    const localSubsurveys = enumerateSubsurveys(survey);
    setSubsurveys(localSubsurveys);
  }, [survey]);


  // If the active survey is not in the available subsurveys, set it to null
  // Note this MUST be below the initialLoad call or we won't
  // have backwards compatibility with /config#entireprogram
  useEffect(() => {
    if (subsurveys?.length && activeSurvey) {
      const found = subsurveys?.find(s => s.path === activeSurvey);
      if (!found) {
        setActiveSurvey(null);
      }
    }
  }, [subsurveys, activeSurvey]);

  async function doEnableCollab() {
    if (confirm("Are you sure you want to enable Collab?")) {
      setTogglingCollab(true);
      if (survey) {
        await enableCollab(survey);
      } else {
        toast({
          description: 'Error enabling collab: No survey found',
          variant: 'error'
        });
      }
      setTogglingCollab(false);
    }
  }

  async function doDisableCollab() {
    if (confirm(`Note that all unpublished changes will be lost. ALL branches will also be permanently deleted. Continue?`)) {
      setTogglingCollab(true);
      await disableCollab();
      setTogglingCollab(false);
    }
  }

  const syncNonCollab = useCallback(async (): Promise<string | null> => {
    if (!surveyName || !loadedVersionId || !survey) return null;

    setSyncing(true);
    try {
      const result = await rebaseNonCollabPost({
        survey: surveyName,
        content: JSON.stringify(survey),
        parentVersionId: loadedVersionId,
      });

      if (!result) return null;

      if ('error' in result) {
        return null;
      }

      const mergedSurvey = JSON.parse(result.mergedContent);
      distroRef.current?.initialize(
        mergedSurvey,
        { ...user, name: config?.user?.name, tabId },
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        { enableKeyIsValid, debug: false, collabEnabled: false }
      );
      setLoadedVersionId(result.publishedVersionId);
      setLatestVersionId(result.publishedVersionId);
      toast({ description: 'Synced with latest published version. Your changes have been merged.' });
      return result.mergedContent;
    } finally {
      setSyncing(false);
    }
  }, [surveyName, loadedVersionId, survey, rebaseNonCollabPost, user, config, tabId, toast]);

  const publish = useCallback(async ({skipDistroValidation = false}: {skipDistroValidation?: boolean} = {}) => {
    if (!canPublish || !survey || collabBusy) return;
    if (!skipDistroValidation) {
      const distroValidationResult = validateDistro(survey);
      if (distroValidationResult.length > 0) {
        if (distroValidationResult.some(result => result.severity === 'error')) {
          setDistroValidationErrorsForModal(distroValidationResult);
          setPublishing(false);
          return;
        } else {
          toast({
            description: <div>
              Publishing with warnings,
              please check the <button className="text-blue-500 underline cursor-pointer" onClick={() => selectPreviewTab("validationSummary", 'validation_link')}>Validation panel</button>.
            </div>,
            variant: 'default',
            icon: <Icon name="ExclamationTriangleIcon" type="solid" />
          })
        }
      }
    }
    let surveyStringForPublish = JSON.stringify(survey);

    if (collabEnabled && !confirm("Collab is enabled. Have you checked with your collaborators before publishing?")) {
      setPublishing(false);
      return;
    }

    let distroConfigToSave = distroConfigName;
    if (!distroConfigToSave) {
      const newlyEnteredName = prompt('Enter a name to save');
      if (newlyEnteredName) {
        distroConfigToSave = newlyEnteredName;
        setDistroConfigName(newlyEnteredName);
      }
    }
    if (!distroConfigToSave) {
      setPublishing(false);
      return;
    }
    // Ask user for a commit message to be displayed on the versions page.
    const commitMessage = prompt("Please enter a version description (Optional)");
    if (commitMessage === null) {
      setPublishing(false);
      return;
    }
    setPublishing(true);
    try {
      const roboscreenerValidationResponse = await validateSurveyRS({ survey: surveyStringForPublish });

      if (!roboscreenerValidationResponse || 'error' in roboscreenerValidationResponse) {
        setRoboscreenerValidationErrors([roboscreenerValidationResponse.error || 'An error occurred']);
        setPublishing(false);
        return;
      } else if (roboscreenerValidationResponse.value.errors) {
        setRoboscreenerValidationErrors(roboscreenerValidationResponse.value.errors);
        setPublishing(false);
        return;
      }
    } catch (e) {
      console.log(e);
      setPublishing(false);
      return;
    }

    if (!collabEnabled) {

      // Check if the live survey has changed in remote since it was loaded;
      // if so, attempt a 3-way auto-merge before publishing.
      let surveyMetadataResp: { hash: string, lastModified: string, versionId: string } | undefined;
      try {
        surveyMetadataResp = await loadSurveyMetadata({ name: distroConfigToSave, purpose: "checking survey has not changed" });
        if (loadedVersionId && surveyMetadataResp.versionId !== loadedVersionId) {
          const mergedContent = await syncNonCollab();
          if (!mergedContent) {
            const confirmOverwrite = confirm("WARNING! Could not safely sync. Do you want to OVERWRITE the published changes with your own?");
            if (!confirmOverwrite) {
              setPublishing(false);
              return;
            }
          } else {
            // Re-validate merged content through roboscreener before publishing
            try {
              const mergeValidation = await validateSurveyRS({ survey: mergedContent });
              if (!mergeValidation || 'error' in mergeValidation) {
                setRoboscreenerValidationErrors([('error' in mergeValidation ? mergeValidation.error : undefined) || 'An error occurred validating merged content']);
                setPublishing(false);
                return;
              } else if (mergeValidation.value.errors) {
                setRoboscreenerValidationErrors(mergeValidation.value.errors);
                setPublishing(false);
                return;
              }
              surveyStringForPublish = mergedContent;
            } catch (e) {
              setRoboscreenerValidationErrors(['An unexpected error occurred validating merged content']);
              setPublishing(false);
              return;
            }
          }
        }
      } catch (e) {
      // It's only best effort;
      }

      // Check for local updates before saving to reduce clutter in versions page
      const currentHash = await (async () => {
        const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(surveyStringForPublish));
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      })();
      if (surveyMetadataResp?.hash !== currentHash) {
        const publishProgressKey = generateRandomString(16, 'abcdefghjkmnpqrstuvwxyz0123456789');
        publishProgress.startPolling(publishProgressKey);

        const response = await saveSurvey({
          name: distroConfigToSave,
          content: surveyStringForPublish,
          browserTab: tabId,
          commitMessage,
          publishProgressKey,
          source: 'distro',
          action: 'publish',
        });

        publishProgress.stopPolling();

        if ('status' in response && response.status === 'ok') {
          stateRef.current!.unpublishedChanges = [];
          setSurvey(prev => (prev ? {...prev} : null));
          publishProgress.setDone();
          if (stateRef.current) {
            stateRef.current.unpublishedChanges = [];
          }
        } else if ('reviewRequired' in response && response.reviewRequired) {
          publishProgress.hide();
          // API returned that notification review is required
          setReviewRequired(response.result);
          setPublishing(false);
          return;
        } else {
          publishProgress.setError(JSON.stringify(response));
        }
      } else {
        toast({
          description: <span>Everything's up to date!<br /><br />No changes to save.</span>,
        })
      }

      // Collab Mode should always attempt a publish if it's tried, even if the survey looks the same as the last published version.
      // We may have missed other's changes, and do not want to overwrite those.
    } else if (collabEnabled) {
      // Send only unpublished changes (delta since last publish).
      // The server probably already has most or all of these, but because of eventual consistency,
      // the client might be more up-to-date than the server, so this ensures the publish has everything we are seeing.
      const toSend = JSON.stringify(reduceEvents([], stateRef.current!.unpublishedChanges || []));
      const publishProgressKey = generateRandomString(16, 'abcdefghjkmnpqrstuvwxyz0123456789');
      publishProgress.startPolling(publishProgressKey);

      const res = await writeNewCollabVersion({ toSend, publish: true, commitMessage, publishProgressKey, action: 'publish' });

      publishProgress.stopPolling();

      if (res?.status === 'ok') {
        const collabResp = await getCollabState();
        stateRef.current!.unpublishedChanges = collabResp.unpublishedEvents;
        // This triggers a setSurvey that will set our survey state variable 
        // to the most up to date collab version.
        distroRef.current?.mergeEvents(collabResp.events);
        alertJustPublished();
        publishProgress.setDone();
      } else if (res?.error === 'branch_behind_published') {
        publishProgress.setError('Branch is behind published. Please sync.');
      } else {
        publishProgress.setError(res.error || 'Failed to save survey. Please try again.');
      }
    }

    const metadataResponse = await loadSurveyMetadata({ name: distroConfigToSave, purpose: "final load after save" });
    setPublishing(false);
    setLoadedVersionId(metadataResponse.versionId ?? '');
    setLatestVersionId(metadataResponse.versionId ?? '');
    setLastPublished(metadataResponse.lastModified);
  }, [
    distroConfigName, collabEnabled, survey, tabId, config, canPublish, loadedVersionId, getCollabState, collabBusy, toast, syncNonCollab
  ]);

  const isSafeToStartReview = collabEnabled || 
    (commitId 
      ? (!latestCommitMetadata || commitId === latestCommitMetadata.id) 
      : (latestVersionId === loadedVersionId && latestCommitMetadata?.reviewStatus !== "review_pending" && latestCommitMetadata?.reviewStatus !== "approved")
    );
  
  const startDistroReviewInternal = useCallback(async (reviewWindow?: Window | null) => {
    if (!canPublish || !survey || collabBusy) return;
    setPublishing(true);

    try {
      // Ensure we're passing an ExpandedSurvey object to the API
      let surveyToReview: ExpandedSurvey | undefined;
      let baseVersionId: string | undefined;
      let collabVersion: number | undefined;
      if (collabEnabled && branchId) {
        // Ensure that all Collab events currently in Dynamo are saved in S3 before
        // starting Review
        const snapshotCollab = await saveCollabDynamoEvents({ name: distroConfigName, branchId, source: 'distro', action: 'snapshot' });

        if ('error' in snapshotCollab) {
          reviewWindow?.close(); // Close the window on error
          toast({
            description: 'Error starting review: ' + snapshotCollab.error,
            variant: 'error'
          });
          setPublishing(false);
          return;
        }

        baseVersionId = snapshotCollab.baseVersionId;
        collabVersion = snapshotCollab.collabVersion;
      } else {
        surveyToReview = survey;
      }

      const response = await startDistroReviewReq({
        baseVersionId,
        collabVersion,
        survey: surveyToReview ? JSON.stringify(surveyToReview) : undefined,
        surveyName: distroConfigName,
        branchId,
        parentVersion: loadedVersionId || undefined,
      });

      if (response && response.reviewId) {
        // Navigate the already-opened window to the review page
        const reviewUrl = `/distro-review/${response.reviewId}`;
        if (reviewWindow && !reviewWindow.closed) {
          reviewWindow.location.href = reviewUrl;
        } else {
          // Fallback: if window was blocked/closed, try opening again
          window.open(reviewUrl, '_blank', 'noopener,noreferrer');
        }
        
        if (response.commitId && !collabEnabled) {
          const url = new URL(window.location.href);
          url.searchParams.set('commit', response.commitId);
          window.history.replaceState({}, '', url.toString());
          setCommitId(response.commitId);
        }
      } else {
        reviewWindow?.close();
        toast({
          description: 'Failed to start review process',
          variant: 'error'
        });
      }
    } catch (e) {
      console.error('Error starting review:', e);
      reviewWindow?.close();
      toast({
        description: 'Error starting review process',
        variant: 'error'
      });
    }

    setPublishing(false);
  }, [survey, canPublish, collabBusy, toast, collabEnabled, branchId, distroConfigName, saveCollabDynamoEvents, startDistroReviewReq, loadedVersionId]);

  const handleStartDistroReview = useCallback(() => {
    if (!canPublish || !survey || collabBusy) return;
    if (!isSafeToStartReview) {
      setStartReviewConflictModalOpen(true);
      return;
    }
    
    // Open the window immediately to avoid popup blocking
    // Note: Not using 'noopener,noreferrer' features as some browsers return null with that parameter
    const reviewWindow = window.open('/redirect', '_blank');
    startDistroReviewInternal(reviewWindow);
  }, [canPublish, survey, collabBusy, isSafeToStartReview, startDistroReviewInternal]);

  const startReviewButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const callback = (e: KeyboardEvent) => {
      // Only the visible tab owns the global shortcut; otherwise every mounted editor would
      // fire it and start a review from a hidden tab's (display:none) button.
      if (!isActiveInstance) return;
      if (matchesExactKey(e, createPlatformShortcut('s'))) {
        e.preventDefault();
        startReviewButtonRef.current?.focus();
        setTimeout(() => startReviewButtonRef.current?.click(), 1);
      }
    }
    document.addEventListener('keydown', callback)
    return () => { document.removeEventListener('keydown', callback) }
  }, [canPublish, publishing, publish, isActiveInstance]);

  const searchEditorContainer = useRef<HTMLDivElement>(null);
  const [showSearch, setShowSearch] = useState(false);
  function doShowSearch() {
    setShowSearch(true);
  }

  /**
   * "Save as" the current survey. If the new name already exists, the user is prompted to overwrite it.
   * If the new name does not exist, the survey is copied to the new name.
   */
  async function rename() {
    let n = distroConfigName;
    let _n = prompt('Enter a new name');
    let doesExist;
    if (_n) {
      n = _n;
      doesExist = await doesSurveyExist({ name: n });

      // If does exist, we want to make sure to default to not saving
      let yesSave = !doesExist;
      if (doesExist) {
        yesSave = confirm(`A survey already exists with name: ${n}\nDo you wish to overwrite it?`);
      }

      if (yesSave) {
        setDistroConfigName(_n);
        setPublishing(true);
        const response = await saveSurvey({ name: n, content: JSON.stringify(survey || { survey: [], notifications: [], personas: [], config: { version: 'v1', name: get_deployment() } }), browserTab: tabId, source: 'distro', action: 'publish' });

        if ('status' in response && response.status === 'ok') {
          toast({
            description: `Successfully saved new survey: ${n}`,
            variant: 'success'
          })
        } else if ('reviewRequired' in response && response.reviewRequired) {
          // API returned that notification review is required
          setReviewRequired(response.result);
          setPublishing(false);
          return;
        } else {
          toast({
            description: JSON.stringify(response),
          })
        }

        setPublishing(false);
        window.location.pathname = '/config/' + n;
      }
    }
  }

  // Create a brand-new, empty survey under a fresh name and open it. Unlike rename()/"Save
  // Survey As…" (which persists the *current* survey's content), this always writes an empty
  // survey so it reads as "new", not "duplicate". Opens it as a tab when the tab strip is
  // present; otherwise navigates to it.
  async function createNewSurvey() {
    const name = prompt('Enter a name for the new survey');
    if (!name) return;

    if (await doesSurveyExist({ name })) {
      toast({
        description: `A survey already exists with name: ${name}`,
        variant: 'error',
      });
      return;
    }

    setPublishing(true);
    const emptyContent = JSON.stringify({
      survey: [],
      notifications: [],
      personas: [],
      config: { version: 'v1', name: get_deployment() },
    });
    const response = await saveSurvey({ name, content: emptyContent, browserTab: tabId, source: 'distro', action: 'publish' });
    setPublishing(false);

    if ('status' in response && response.status === 'ok') {
      toast({
        description: `Created new survey: ${name}`,
        variant: 'success',
      });
      if (props.onAddTab) {
        props.onAddTab(name);
      } else {
        window.location.pathname = '/config/' + name;
      }
    } else {
      toast({
        description: JSON.stringify(response),
      });
    }
  }

  function injestTranslations() {
    if (translations) {
      // Match against the clone extractDedupedTranslations last tagged with `_id`s.
      // Cloning Distro's live `read()` tree here would drop ids assigned only during
      // a later scope change (e.g. include-hidden), and importTranslations would
      // silently skip those strings. Clone so importTranslations mutates a disposable copy.
      const surveyToImport = structuredClone(translationExtractRef.current ?? survey);
      importTranslations(surveyToImport, translations);
      console.log("Initializing distro with injestTranslations()", surveyToImport);
      distroRef.current?.initialize(surveyToImport, { ...user, name: config?.user?.name, tabId });
      translationExtractRef.current = null;
      alert("Injected translations, please save!");
    }
  }

  function openTranslationsModal() {
    if (collabEnabled) {
      toast({
        description: "You must disable collab to edit translations",
        variant: 'error'
      })
      return;
    }
    if (confirm('This will save the current state of your survey. Continue?')) {
      // `survey` is Distro's memoized, shared read() tree; extractDedupedTranslations
      // assigns `_id` onto every translatable node in place, so it must operate on a
      // disposable copy rather than the cache. We then re-initialize Distro with that
      // SAME copy (ids included) and publish, which is how the new ids actually persist.
      const surveyToExtract = structuredClone(survey);
      const extractedTranslations = extractDedupedTranslations(surveyToExtract);
      translationExtractRef.current = surveyToExtract;
      console.log("Initializing distro with openTranslationsModal()", surveyToExtract);
      distroRef.current?.initialize(surveyToExtract, { ...user, name: config?.user?.name, tabId });
      // We need to save state after extracting so the ids persist.
      publish();
      setTranslations(extractedTranslations);
      initialTranslations.current = JSON.stringify(extractedTranslations);
      setTranslationModalOpen(true);
    }
  }

  function handleTranslationScopeChange(selectedRoots: any[], includeHidden: boolean) {
    // Clone the FULL survey so newly assigned `_id`s (e.g. hidden nodes that
    // weren't tagged on open) land on a document injestTranslations can match
    // against. Cloning only `selectedRoots` would tag a discarded subset.
    const surveyToExtract = structuredClone(survey);
    const liveComponents = Array.isArray(survey) ? survey : survey?.survey ?? [];
    const clonedComponents = Array.isArray(surveyToExtract) ? surveyToExtract : surveyToExtract?.survey ?? [];
    // Definition/Definition Bucket items come from the sibling `survey.definitions` array
    // (see getTopLevelItems), so a selected item may need to be located there instead.
    const liveDefinitions = Array.isArray(survey) ? [] : survey?.definitions ?? [];
    const clonedDefinitions = Array.isArray(surveyToExtract) ? [] : surveyToExtract?.definitions ?? [];
    const extractRoot = selectedRoots.length === 0
      ? surveyToExtract
      : selectedRoots
        .map((selected) => {
          const componentIndex = liveComponents.indexOf(selected);
          if (componentIndex !== -1) return clonedComponents[componentIndex];
          const definitionIndex = liveDefinitions.indexOf(selected);
          if (definitionIndex !== -1) return clonedDefinitions[definitionIndex];
          return undefined;
        })
        .filter((node) => node != null);
    const extractedTranslations = extractDedupedTranslations(extractRoot, new Set(), includeHidden);
    translationExtractRef.current = surveyToExtract;
    setTranslations(extractedTranslations);
    initialTranslations.current = JSON.stringify(extractedTranslations);
  }

  function openContentTemplateModal() {
    if (confirm('This will save the current state of your survey. Continue?')) {
      // publish first. That makes sure that when we save content templates on the backend, 
      // no unpublished changes on the FE will be overwritten.
      publish();
      setContentTemplateModalOpen(true);
    }
  }

  function ConvertToObject() {
    setSurvey({
      survey: survey as any,
      notifications: [],
      personas: [],
    })
  }

  async function loadInfoFromApplicant() {
    const uid = prompt("Please enter a UID of an applicant");
    if (uid) {
      setMockInfo((await getEverything({ uid })).info)
    }
  }

  function CreateNew() {
    distroRef.current?.initialize({
      survey: [],
      personas: [],
      config: {
        version: 'v1',
        name: get_deployment(),
      },
    }, { ...user, name: config?.user?.name, tabId });
  }

  (window as any).magic = async (data: any) => {
    if (!data.question) {
      throw new Error('no question asked');
    }
    console.info('doing magic with data', data);
    // if (data.qualifier?.toLowerCase() === 'persona') {
    //   const prompt = data.question;
    //   const makeResult = (content: any) => ({ choices: [{ message: { content: JSON.stringify(content) } }] })

    //   if (isNaN(parseInt(prompt))) {
    //     return makeResult(await createPersona(10, prompt))
    //   }

    //   const persons = Array(parseInt(prompt)).fill(null).map(() => createPersona(10));

    //   return makeResult(
    //     (await Promise.allSettled(persons))
    //       .filter(r => r.status === 'fulfilled')
    //       .map((r: any) => r.value)
    //   );
    // }
    return await doMagic(data);
  }

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  // This code enables the resizing of the left and right panes
  const [rightPaneWidth, setRightPaneWidth] = useState(33.3);
  const resizingRef = useRef(false);
  const onMouseDown = useCallback((e: MouseEvent) => {
    e.preventDefault()
    resizingRef.current = true;
  }, []);

  const onMouseUp = useCallback((_e: MouseEvent) => {
    resizingRef.current = false;
  }, []);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      if (!resizingRef.current) return;

      const container = e.currentTarget as HTMLElement;
      const newWidth = ((container.clientWidth - e.clientX) / container.clientWidth) * 100;

      setRightPaneWidth(newWidth);
    },
    [],
  );


  const [resetProgramModalOpen, setResetProgramModalOpen] = useState(false);

  async function reset() {
    setResetProgramModalOpen(true);
  }

  // These provide the children (e.g. ModularQuestionPage) with a ref to the scroll container
  // and a function to scroll to the top
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      if (typeof scrollContainerRef.current.scrollTo === 'function') {
        scrollContainerRef.current.scrollTo(0, 0);
      } else {
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  };

  // Together notifsByFieldRef and notifDetailsByPrefixRef replace the need to maintain the large
  // DependencyMap with a tiny deduplicated structure:
  // - notifsByFieldRef: field -> array of targetPrefixes
  // - notifDetailsByPrefixRef: the unique notification objects, stored once
  const notifsByFieldRef = useRef<Map<string, string[]> | null>(null);
  const notifDetailsByPrefixRef = useRef<Map<string, { name: string, targetPrefix: string }> | null>(null);
  // Document-wide map of definitionId -> Definition, used to resolve the references a Templated Block
  // can contain when previewing its resolved result
  const definitionsByIdRef = useRef<Map<string, DefinitionComponent> | null>(null);
  // Keys ever pushed into the NavigationTargetPreview external data below — tracked so that if a
  // target disappears from the index on a later rebuild (e.g. the referenced node was deleted or
  // renamed), we push `null` for it instead of leaving the last-known-good preview stale forever.
  const navTargetKeysRef = useRef<Set<string>>(new Set());

  // Renderers map - stable reference to avoid Distro re-renders.
  // Used for CustomUI components (rendering extra UI on top of Distro components)
  const renderers = useMemo<CustomUIRenderers<any, any>>(() => ({
    ApplicantInfoMatchWarning: {
      render: (value: ApplicantInfoMatchLookup) => {
        // This CustomUI exists to present a warning for when the filter is not set.
        if (value.filter) return null;
        return (
          <div className='px-2 bg-[#fef3c6]'>
            ⚠️ You are strongly encouraged to set a filter when using an ApplicantInfoMatch lookup to avoid performance issues.
            You should test this query out in /explore before adding it to Dashboards.
          </div>
        )
      }
    },
    NavigationTargetPreview: {
      getKey: (value: NavigationReferenceNode) => value?.target || null,
      render: (value: NavigationReferenceNode, node?: NavigationNode | null) => {
        if (!value?.target) return null;
        if (node === undefined) {
          // Index hasn't finished building yet (debounced up to 5s after the last edit) —
          // stay quiet rather than flashing a false "no match" warning.
          return <div className="text-[10px] text-gray-400 italic">Resolving target…</div>;
        }
        if (node === null) {
          return <div className="text-[10px] text-amber-700">⚠ No auto-derived nav node matches this target.</div>;
        }
        const displayName = typeof node.name === 'string' ? node.name : node.name?.en;
        return (
          <div className="w-full p-1 bg-green-50 border border-green-200">
            <div className="text-xs font-semibold text-green-700">
              <div className="flex items-center gap-1 text-[10px] text-gray-600">
                <span className="rounded-full px-2 py-0.5" style={{ backgroundColor: stringToColor(node.kind ?? '', DISTRO_DEFAULT_LIGHTNESS) }}>{node.kind}</span>
                <span>{displayName || '(unnamed)'}</span>
              </div>
            </div>
          </div>
        );
      }
    },
    TemplatedBlockPreview: {
      render: (value: TemplatedBlock) => {
        if (!value?.iterations) return null;
        let iterations: { key: string, value: string }[][];
        try {
          iterations = resolveTemplatedBlockIterations(value.iterations, definitionsByIdRef.current ?? undefined);
        } catch (e) {
          console.error('Error resolving Templated Block preview', e);
          return <div className="w-full p-3 text-xs text-red-600">Unable to resolve preview.</div>;
        }
        return <TemplatedBlockPreview iterations={iterations} />;
      }
    },
    Computed: {
      render: (value: Computed) => {
        const affectedNotifs = (notifsByFieldRef.current?.get(value?.targetField) ?? [])
          .map(prefix => notifDetailsByPrefixRef.current?.get(prefix))
          .filter((n): n is { name: string, targetPrefix: string } => !!n);
        if (affectedNotifs.length > 0) {
          return (
            <Accordion
              className="w-full"
              type="single"
            >
              <AccordionItem value="item-1">
                <AccordionTrigger className='px-2 bg-[#fef3c6]'>
                  ⚠️ There are notifications that depend on this computed field!
                </AccordionTrigger>
                <AccordionContent>
                  <div className='bg-[#fef3c6] rounded p-3'>
                    <ul>
                      {(affectedNotifs || []).map(affectedNotif => (
                        <li id={affectedNotif.targetPrefix} key={affectedNotif.targetPrefix}>
                          <strong>{affectedNotif.name}</strong>  ({affectedNotif.targetPrefix})
                        </li>
                      ))}
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          );
        }
        return null;
      }
    }
  }), []);

  const { customUI: customUIValue, updateData } = useCustomUI<any, any>(renderers);

  // Used for the Computed field downstream-notification warning in customUIValue —
  // not on the critical render path, so a long debounce is safe.
  useEffect(() => {
    if (!survey) {
      notifsByFieldRef.current = null;
      notifDetailsByPrefixRef.current = null;
      definitionsByIdRef.current = null;
      Array.from(navTargetKeysRef.current).forEach(key => updateData('NavigationTargetPreview', key, null));
      navTargetKeysRef.current = new Set();
      return;
    }
    const timer = setTimeout(() => {
      // survey is already expanded in state (set via setSurvey(expanded) in handleUpdatesInner);
      // only re-expand if somehow we have a non-expanded survey (e.g. ConvertToObject path)
      const expanded = (survey as any).__templates_expanded
        ? (survey as ExpandedSurvey & { __templates_expanded: true })
        : expandTemplatesStrict(survey);
      try {
        // Build the full dep map as a local variable so it can be GC'd after this block.
        const depMap = getDependencyMap(expanded);
        const fieldLookup = new Map<string, string[]>();
        const detailsLookup = new Map<string, { name: string, targetPrefix: string }>();
        const mapKeys = Array.from(depMap.keys());
        for (const fieldRef of mapKeys) {
          const notifs = findAnyDownstreamNotifications(depMap, fieldRef);
          if (notifs.length > 0) {
            fieldLookup.set(fieldRef, notifs.map(n => {
              // Store notification details once, keyed by targetPrefix.
              if (!detailsLookup.has(n.targetPrefix)) {
                detailsLookup.set(n.targetPrefix, { name: n.name, targetPrefix: n.targetPrefix });
              }
              return n.targetPrefix;
            }));
          }
        }
        notifsByFieldRef.current = fieldLookup;
        notifDetailsByPrefixRef.current = detailsLookup;
      } catch (e) {
        console.error('Error calculating dependency notifications', e);
        notifsByFieldRef.current = null;
      }
      try {
        definitionsByIdRef.current = extractDefinitions(survey.definitions || [])
      } catch (e) {
        console.error('Error extracting definitions', e);
        definitionsByIdRef.current = null;
      }
      try {
        const newIndex = buildNavTargetIndex(expanded);
        const newKeys = new Set(Array.from(newIndex.keys()));
        const allKeys = new Set(Array.from(navTargetKeysRef.current).concat(Array.from(newKeys)));
        Array.from(allKeys).forEach(key => {
          updateData('NavigationTargetPreview', key, newIndex.get(key) ?? null);
        });
        navTargetKeysRef.current = newKeys;
      } catch (e) {
        console.error('Error building navigation target index', e);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [survey, updateData]);

  const deploymentName = useMemo(() => get_deployment(), []);
  const surveyChannel = useMemo(
    () => distroConfigName ? get_deployment() + ":" + distroConfigName : '',
    [distroConfigName]
  );

  // Every preview pane and its label. What's actually in the tab bar is `visiblePreviewTabKeys`.
  const previewTabs = useMemo<{ key: PreviewTab, label: string }[]>(() => [
    { key: 'survey', label: 'Survey' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'payments', label: 'Payments' },
    { key: 'targetFields', label: 'Target Fields' },
    { key: 'dashboards', label: 'Dashboards' },
    { key: 'testCases', label: 'TestCases' },
    { key: 'robonav', label: 'RoboNav' },
    { key: 'changes', label: 'Changes' },
    { key: 'fieldMismatches', label: 'Field Mismatches {' + Object.keys(fieldMismatches).length + '}' },
    { key: 'validationSummary', label: 'Validation' },
    { key: 'navigation', label: 'Navigation' },
    { key: 'applicantPortal', label: 'Applicant Portal' },
    ...(survey?.config?.comms?.automaticMessageHandler
      ? [{ key: 'amh' as const, label: 'Automatic Message Handler' }]
      : []),
  ], [fieldMismatches, survey?.config?.comms?.automaticMessageHandler]);

  const visiblePreviewTabs = visiblePreviewTabKeys
    .map((key) => previewTabs.find((tab) => tab.key === key))
    // Drops tabs that no longer exist, e.g. a stale key from storage or AMH once it's turned off.
    .filter((tab): tab is { key: PreviewTab, label: string } => !!tab);
  // Everything the overflow menu can toggle, listed in the canonical order rather than the bar's.
  const togglablePreviewTabs = previewTabs.filter((tab) => tab.key !== PERMANENT_PREVIEW_TAB);

  // Selection can point at a tab that isn't in the bar, e.g. after switching between survey kinds.
  const activePreview = visiblePreviewTabKeys.includes(selectedPreview)
    ? selectedPreview
    : PERMANENT_PREVIEW_TAB;

  // Chrome-style tabs render above the whole editor when the ConfigPage wrapper supplies them.
  const showTabs = !!props.tabs && props.tabs.length > 0;
  // Selected tab matches the bar below (green in dev, blue in prod); others are a muted shade.
  // In prod, adminpanel gets purple so it can't be mistaken for entireprogram.
  const isDevEnv = process.env.NODE_ENV === 'development';
  const activeTabClass = (tabName: string) => isDevEnv
    ? 'bg-green-200 text-green-900 border-green-300'
    : tabName === SurveyName.ADMIN_PANEL
      ? 'bg-purple-200 text-purple-900 border-purple-300'
      : 'bg-blue-200 text-blue-900 border-blue-300';
  const inactiveTabClass = (tabName: string) => isDevEnv
    ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
    : tabName === SurveyName.ADMIN_PANEL
      ? 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
      : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';
  // The bar below the tab strip matches the active tab's color (also purple for adminpanel in
  // prod). Without tabs (e.g. the macro editor) it keys off the survey being edited.
  const activeSurveyName = props.activeTab ?? distroConfigName;
  const barBgClass = isDevEnv
    ? 'bg-green-200'
    : activeSurveyName === SurveyName.ADMIN_PANEL ? 'bg-purple-200' : 'bg-blue-200';
  // The "+" menu offers every known survey that isn't already open as a tab.
  const openTabSet = new Set(props.tabs ?? []);
  const addableSurveyPaths = (surveys && Array.isArray(surveys) ? surveys : [])
    .filter((s): s is string => !!s)
    .map((s) => s.replace('dso-' + get_deployment() + '-', '').replace('.json', ''))
    .filter((path) => !openTabSet.has(path));

  const programNameAndStage = <div className="flex flex-row items-center gap-2 mx-3">
    <div className="font-bold text-blue-400">{`[${deploymentName}] `}</div>
    <ProgramPhaseIndicator />
  </div>

  return <>
    <UserInfoContext.Provider value={user}>
      {showTabs && (
        // Chrome-style tab strip above all per-survey chrome, so it reads as "everything below
        // belongs to the active tab". Each open survey is a tab; "+" opens another; each tab is
        // its own independent editor.
        <div className="flex items-center gap-1 h-9 px-2 pt-1 overflow-x-auto overflow-y-hidden whitespace-nowrap bg-white" role="tablist" aria-label="Open surveys">
          {programNameAndStage}
          {props.tabs!.map((name) => {
            const active = name === (props.activeTab ?? distroConfigName);
            // Defaults (entireprogram/adminpanel) are permanent; never offer to close them,
            // nor the last remaining tab.
            const closable = !isDefaultSurvey(name) && props.tabs!.length > 1;
            return (
              <div
                key={name}
                role="tab"
                aria-selected={active}
                title={name || '[untitled]'}
                onClick={() => props.onSelectTab?.(name)}
                className={'group flex items-center gap-1 max-w-[14rem] px-3 py-1 rounded-t-md cursor-pointer text-sm border-x border-t self-end '
                  + (active ? (activeTabClass(name) + ' font-medium -mb-px relative z-[41]') : '')}
              >
                <span className="truncate">{name || '[untitled]'}</span>
                {closable && (
                  <button
                    type="button"
                    aria-label={`Close ${name}`}
                    onClick={(e) => { e.stopPropagation(); props.onCloseTab?.(name); }}
                    className="border-0 bg-transparent p-0 leading-none opacity-60 hover:!opacity-100"
                  >
                    <Icon name="XMarkIcon" type="solid" size="sm" />
                  </button>
                )}
              </div>
            );
          })}
          <ActionsDropdown
            item={null}
            anchor="bottom start"
            className={'self-center flex items-center justify-center h-6 w-6 p-0 ' + (isDevEnv ? '!text-green-700 hover:!text-green-800' : '!text-blue-600 hover:!text-blue-800')}
            triggerIcon={<Icon name="PlusIcon" type="solid" size="sm" role="decorative" />}
            actions={[
              ...(addableSurveyPaths.length
                ? addableSurveyPaths.map((path) => ({ label: path, onClick: () => props.onAddTab?.(path) }))
                : [{ label: 'All surveys already open', onClick: () => {}, disableWhen: () => true }]),
              { label: 'Create new survey…', icon: 'PlusIcon' as const, onClick: () => createNewSurvey() },
            ]}
          />
        </div>
      )}
      <div className={"flex h-14 border-b border-blue-300 z-40 relative top p-1 " + barBgClass}>
        <div className="font-bold text-2xl text-blue-400 ml-2 flex items-center">
          {/* When tabs are shown the survey name lives in the active tab above; otherwise (e.g.
            the macro editor) show it inline so the bar still identifies the current survey. */}
          {!showTabs && programNameAndStage}
          {!showTabs && distroConfigName && <span className="text-blue-600 text-2xl mr-2">{distroConfigName}</span>}
          {!survey && !distroConfigName && <button className="mr-2" onClick={CreateNew}>Create New</button>}
          {collabEnabled && branches && branches.length > 0 && surveyChannel && (
            <span className="text-base font-normal">
              <FacePile
                name={config?.user?.name}
                channel={surveyChannel}
                browserTab={tabId}
                extraData={() => ({ branchName })}
                filter={(u) => !!u.extra?.branchName && u.extra?.branchName !== branchName}
                disabled={!isActiveInstance}
              />
            </span>
          )}
        </div>
        {collabEnabled && branches && branches.length > 0 && (
          <div className="flex items-center">
            <BranchDropdown
              branches={branches}
              currentBranchId={branchId}
              onCreateBranch={async (name) => { const result = await createBranch(name); if (result) await switchBranch(result.branchId); }}
              onSwitchBranch={(id) => switchBranch(id)}
              onDeleteBranch={(id) => deleteBranch(id)}
              onResetToPublished={async () => { await resetBranchToPublished(); }}
              disabled={publishing || !!collabBusy}
            />
          </div>
        )}
        <div className="flex items-center">
          <FacePile name={config?.user?.name} channel={channel} browserTab={tabId} onUserRemoved={handleUserRemoved} disabled={!isActiveInstance} unsavedChanges={() => {
            if (collabEnabled) return false;
            return stateRef.current?.unpublishedChanges?.length ? stateRef.current.unpublishedChanges.length > 0 : false;
          }} />
        </div>
        {commitId && <div className="m-1 text-sm flex items-center h-full text-center">
          <button className="border-0 flex items-center bg-transparent ml-2 mr-3 text-blue-600 hover:text-gray-600 hover:line-through font-normal"
            aria-label="Reload without base commit" onClick={(e) => {
            //remove search params from url
              e.stopPropagation();
              const url = new URL(window.location.href);
              url.searchParams.delete('commit');
              window.history.replaceState({}, '', url.toString());
              window.location.reload();
            }}>
            <span>
              Base Commit: {commitId}
            </span>
          </button>
        </div>}
        <div className="flex-grow" />
        <div className="flex items-center mr-2">
          <button className="bg-blue-50 border-green-200 rounded-md text-sm p-1 hover:bg-blue-300 flex items-center" onClick={doShowSearch} aria-label="Search"><Icon name="MagnifyingGlassIcon" type="solid" size="sm" /></button>
        </div>
        <div className="my-1 flex items-center gap-x-2" title={lastPublished ? `${distroConfigName} Last Published: ${new Date(lastPublished).toLocaleString()}` : ''}>
          <Dropdown
            color="blue" className="border-0 bg-transparent" colorIntensity={200}
            usePopper
            description="Misc" label={<Icon name="Cog6ToothIcon" type="solid" size="sm" />} options={[
              { label: 'Save Survey As…', callback: () => rename() },
              { label: 'Load Info From Applicant', callback: () => loadInfoFromApplicant() },
              { label: 'Manage translations', callback: () => openTranslationsModal() },
              { label: 'Manage Content Templates', callback: () => openContentTemplateModal() },
              { label: 'Toggle Right Panel', callback: () => setShowRightPanel(!showRightPanel) },
              ...(publicConfiguration.experimental?.distroBreadcrumbs ? [{ label: 'Toggle Breadcrumb', callback: () => setShowBreadcrumb(!showBreadcrumb) }] : []),
              { label: 'Reset Program (Delete All Applicants)', callback: () => reset() },
              { label: 'View Distro Reviews', callback: () => window.open(`/distro-reviews`, '_blank') },
              { label: 'View Published History', callback: () => window.open(`/config-versions#${distroConfigName}`, '_blank') },
              ...(collabEnabled ? [{ label: 'View Auto-Saved History', callback: () => {
                const params = branchId ? `?branch=${branchId}` : '';
                window.open(`/collab-versions${params}#${distroConfigName}`, '_blank');
              } }] : []),
              {
                label: 'Get Scope for User', callback: () => {
                  let user = window.prompt('Enter a user email');
                  if (!user) { alert("Must enter a user."); return; }
                  if (!survey) { alert("Survey is not a whole survey"); return; }
                  let scope = CollectScopeForUser(EmailToUserId(user), survey.survey, survey.users);
                  console.log("scope for user", scope);
                  alert("Check the console.");
                }
              },
              { label: 'Enabled Surveys', callback: () => window.open(`/enabled-surveys`, '_blank') },
              { label: 'Export Survey JSON', callback: () => {
                if (!survey) return;
                const blob = new Blob([JSON.stringify(survey, null, 2)], { type: 'application/json' });
                forceInitiateFileDownload(blob, `${distroConfigName || 'survey'}-${Date.now()}.json`);
              }},
              { label: 'Import Survey JSON', callback: () => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.json';
                input.onchange = async (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (!file) return;
                  try {
                    const text = await file.text();
                    const parsed = JSON.parse(text);
                    if (parsed.__templates_expanded) {
                      toast({ description: 'Cannot import an expanded survey', variant: 'error' });
                      return;
                    }
                    const validation = await validateSurvey({ content: JSON.stringify(parsed) });
                    if (!validation || !validation.valid) {
                      toast({ description: 'Invalid survey JSON: ' + (validation as any)?.reason, variant: 'error' });
                      return;
                    }
                    const name = prompt('IMPORTANT: DO NOT IMPORT AN EXPANDED SURVEY.\nEnter a name for the imported survey:');
                    if (!name) return;
                    const exists = await doesSurveyExist({ name });
                    if (exists && !confirm(`A survey already exists with name: ${name}\nDo you wish to overwrite it?`)) return;
                    const result = await saveSurvey({
                      name,
                      content: JSON.stringify(parsed),
                      commitMessage: 'Imported from JSON file',
                      source: 'distro',
                      action: 'publish'
                    });
                    if (result && 'status' in result && result.status === 'ok') {
                      toast({ description: 'Survey imported successfully', variant: 'success' });
                      window.location.href = `/config/${name}`;
                    } else {
                      toast({ description: 'Failed to import survey: ' + JSON.stringify(result), variant: 'error' });
                    }
                  } catch (err) {
                    console.error('[import] caught error:', err);
                    toast({ description: 'Failed to parse JSON file', variant: 'error' });
                  }
                };
                input.click();
              }},
              { label: '(DEBUG) Export Expanded Survey JSON', callback: () => {
                if (!survey) return;
                const expandedSurvey = expandTemplatesStrict(survey);
                const blob = new Blob([JSON.stringify(expandedSurvey, null, 2)], { type: 'application/json' });
                forceInitiateFileDownload(blob, `${distroConfigName || 'survey'}-expanded-${Date.now()}.json`);
              }},
              ...(get_deployment().includes('postgres') || get_deployment().includes('demo') ? [{
                label: 'Force notifications immediately', callback: async () => alert(JSON.stringify(await sendNotifications({})))
              }] : [])
            ]} />
          {togglingCollab && <SpacedSpinner className='mr-2' />}
          {!collabEnabled && <button className={`border-0 rounded-md p-1 ${!togglingCollab && 'hover:bg-blue-300'} bg-blue-100 mr-2`} disabled={togglingCollab} onClick={doEnableCollab}>Enable Collab</button>}
          {collabEnabled && <button className={`border-0 rounded-md p-1 ${!togglingCollab && 'hover:bg-blue-300'} bg-blue-100 mr-2`} disabled={togglingCollab} onClick={doDisableCollab}>Disable Collab</button>}
        </div>
        <div className="flex items-center ml-2">
          <DropdownMenu
            label={config?.user?.name || L.admin.your_account}
            align="end"
            options={[
              // Account settings currently contains only password/passkey security settings.
              ...((publicConfiguration.adminPasswords || publicConfiguration.adminPasskeys || publicConfiguration.mandatoryPasskeys) ? [{ label: "Account Settings", callback: () => { window.open('/admin/account_settings', '_blank', 'noopener,noreferrer'); } }] : []),
              { label: "Log out", callback: () => { history.push("/logout") } }
            ]}
          />
        </div>
        <>
          {latestCommitMetadata && latestCommitMetadata.reviewStatus === 'review_pending' &&
            <a href={`/distro-review/${latestCommitMetadata.reviewId}`} target="_blank" rel="noopener noreferrer" className="border-0 rounded-md text-sm font-medium text-center ml-3 my-1.5 p-1.5 bg-blue-100 text-blue-600 hover:text-blue-800 hover:no-underline hover:bg-blue-300">
              View Pending Review
            </a>
          }
          <button
            onClick={() => showAIChat ? closeAIChat() : setShowAIChat(true)}
            className={'border-0 rounded-md ml-3 my-1.5 p-1.5 ' + (showAIChat ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-600 hover:text-blue-800 hover:bg-blue-300')}
            title="Toggle AI Assistant">
            <Icon name="SparklesIcon" className="h-6 w-6" />
          </button>
          <button ref={startReviewButtonRef}
            onClick={handleStartDistroReview}
            disabled={publishing || !!collabBusy || !canPublish || !distroConfigName}
            className={'border-0 rounded-md ml-3 my-1.5 p-1.5 '
            + ((publishing || !!collabBusy || !canPublish || !distroConfigName)
              ? ' bg-blue-100 text-gray-400 cursor-not-allowed '
              : (!isSafeToStartReview
                ? ' bg-red-100 text-red-600 hover:text-red-800 hover:bg-red-300 '
                : ' bg-blue-100 text-blue-600 hover:text-blue-800 hover:bg-blue-300 '))
            + (!!stateRef.current?.unpublishedChanges?.length ? ' outline outline-4 outline-orange-300 ' : '')}>
            {!publishing ? (
              <div className="relative inline-block">
                <span className="text-sm font-medium flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Start Review
                </span>
              </div>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        </>
        <button
          onClick={async () => {
            setPublishing(true);
            try {
              if (behindPublished) {
                const result = await rebaseBranch();
                if (result && 'mergeResult' in result) {
                  setPendingMergeResult({ ...result, isRebase: true, publishAfterResolve: true });
                  setPublishing(false);
                  return;
                }
              }
              await (publish as () => Promise<void>)();
            } catch {
              setPublishing(false);
            }
          }}
          disabled={publishing || !!collabBusy || !canPublish || !distroConfigName}
          className={'border-0 rounded-md mx-3 my-1.5 bg-blue-100 p-1.5 '
            + ((publishing || !!collabBusy || !canPublish || !distroConfigName)
              ? 'text-gray-400 cursor-not-allowed '
              : 'text-blue-600 hover:text-blue-800 hover:bg-blue-300 ')
            + (!!stateRef.current?.unpublishedChanges?.length ? ' outline outline-4 outline-orange-300 ' : '')}>
          {!publishing ? (
            <div className="relative inline-block">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      </div>
      
      {(behindPublished || (surveyChangedSinceLoaded && !collabEnabled)) && (
        <div className="flex items-center justify-between px-4 py-2 bg-yellow-100 border-b border-yellow-300">
          <span className="text-sm text-yellow-800">
            Published version has been updated. Sync to get latest changes.
          </span>
          <div className="flex items-center gap-2">
            {collabEnabled && (
              <button
                className={'text-sm font-medium text-yellow-800 rounded px-3 py-1 border-0 '
                  + ((publishing || !!collabBusy) ? 'bg-yellow-200/60 opacity-60 cursor-not-allowed' : 'bg-yellow-200 hover:bg-yellow-300')}
                disabled={publishing || !!collabBusy}
                onClick={() => setPublishedChangesModalOpen(true)}>
                Review published changes
              </button>
            )}
            <button
              className={'text-sm font-medium text-yellow-800 rounded px-3 py-1 border-0 '
                + ((publishing || !!collabBusy || syncing) ? 'bg-yellow-200/60 opacity-60 cursor-not-allowed' : 'bg-yellow-200 hover:bg-yellow-300')}
              disabled={publishing || !!collabBusy || syncing}
              onClick={async () => {
                if (collabEnabled) {
                  const result = await rebaseBranch();
                  if (result && 'mergeResult' in result) {
                    setPendingMergeResult({ ...result, isRebase: true });
                  }
                } else {
                  await syncNonCollab();
                }
              }}>
              {(collabBusy === 'rebasing' || syncing) ? 'Syncing...' : collabEnabled ? 'Sync' : 'Attempt Sync'}
            </button>
          </div>
        </div>
      )}

      {(showSearch && survey) &&
        <div className="z-100">
          <ConfigSearch survey={survey} editorRoot={() => searchEditorContainer.current?.querySelector('distro-editor')?.shadowRoot ?? null} close={() => setShowSearch(false)} />
        </div>
      }
      
      {roboscreenerValidationErrors &&
        <RoboscreenerSurveyErrorsModal
          errors={roboscreenerValidationErrors}
          closeModal={() => setRoboscreenerValidationErrors(undefined)}
        />
      }

      {pendingMergeResult && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex flex-col p-4 overflow-hidden">
          <MergeBranchModal
            mergeResult={pendingMergeResult.mergeResult}
            baseState={mergeBaseState}
            incomingState={mergeIncomingState}
            parentState={parentStateRef?.current}
            baseLabel="Published"
            incomingLabel={pendingMergeResult.publishAfterResolve ? "Your Changes" : "Current Branch"}
            branchLabel={branchName}
            publishAfterMerge={!!pendingMergeResult.publishAfterResolve}
            author={{ ...user, name: config?.user?.name, tabId }}
            facePileChannel={channel ? channel + '-merge' : undefined}
            onMerge={async (resolvedEvents) => {
              const shouldPublish = pendingMergeResult.publishAfterResolve;
              const result = await rebaseBranch(resolvedEvents, pendingMergeResult.publishedVersionId);
              if (!result) return;
              if (result.hasConflicts) {
                // Published version changed — refresh modal with updated conflicts
                setPendingMergeResult({ ...result, isRebase: true, publishAfterResolve: shouldPublish });
                return;
              }
              setPendingMergeResult(undefined);
              if (shouldPublish) {
                await (publish as () => Promise<void>)();
              }
            }}
            onCancel={() => setPendingMergeResult(undefined)}
            onResetToPublished={() => {
              setPendingMergeResult(undefined);
              resetBranchToPublished();
            }}
          />
        </div>
      )}

      <div className="flex" style={{ height: `max(0px, calc(100vh - 6.5rem - ${showTabs ? '2.25rem' : '0rem'} - ${showAIChat ? aiChatHeight : 0}px))` }} onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
        {/* Distro editor */}
        <div ref={searchEditorContainer} className="flex-1 h-auto overflow-y-auto relative" style={{ paddingBottom: '40px' }}>
          {collabBusy && (
            <div className="absolute inset-0 bg-white/80 z-50 flex items-center justify-center">
              <div className="flex items-center gap-2 text-blue-600">
                <SpacedSpinner />
                <span>{collabBusy ? CollabBusyLabel[collabBusy] : ''}</span>
              </div>
            </div>
          )}
          <Suspense fallback={<h1 className="p-2">Loading Distro</h1>}>
            <Distro
              ref={distroRef}
              types='src/survey.ts'
              name={distroConfigName === SurveyName.ADMIN_PANEL ? 'AdminPanelSurvey' : 'Root'}
              migrations={MIGRATIONS}
              onChange={handleUpdates}
              programConfig={publicConfiguration}
              getMacro={getMacro}
              macroEditor={props?.macroEditor}
              showBreadcrumb={showBreadcrumb}
              customUI={customUIValue}
              onAskAI={openAIChatForProperty}
            />
          </Suspense>
        </div>
        {/* Resizing bar */}
        <div className="cursor-col-resize w-3" onMouseDown={onMouseDown}>
          <div className="w-1 bg-gray-100 m-auto h-full" />
        </div>
        {/* Preview/Right pane */}
        <div className="group relative h-auto" style={{ width: showRightPanel ? `${rightPaneWidth}%` : 0 }}>
          <SidebarToggle open={showRightPanel} side='right' dir={interfaceContext.direction || 'ltr'} width={rightPaneWidth} onClick={() => setShowRightPanel((showRightPanel) => !showRightPanel)} />
          {showRightPanel &&
          <div className={"overflow-y-auto flex flex-col h-full"} style={{ paddingBottom: '40px' }}>
            <div className="mb-2 bg-white shadow-sm flex flex-wrap items-center">
              {visiblePreviewTabs.map((tab) => {
                const isActive = activePreview === tab.key;
                return <button
                  key={tab.key}
                  className={'border-b-2 border-l-0 border-r-0 border-t-0 bg-transparent inline-block w-auto m-1 ' + (isActive ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent')}
                  onClick={() => selectPreviewTab(tab.key, 'tab_bar')}
                >
                  {tab.label}
                  {tab.key !== PERMANENT_PREVIEW_TAB &&
                    <span
                      aria-label={'Remove ' + tab.label + ' tab'}
                      title={'Remove ' + tab.label + ' tab'}
                      className={'ml-1 inline-flex align-middle ' + (isActive ? 'text-blue-400 hover:text-blue-700' : 'text-gray-300 hover:text-gray-600')}
                      onClick={(e) => { e.stopPropagation(); removePreviewTab(tab.key); }}
                    >
                      <Icon name="XMarkIcon" size="sm" strokeWidth={2} role="decorative" />
                    </span>
                  }
                </button>;
              })}
              {togglablePreviewTabs.length > 0 &&
                <ActionsDropdown
                  item={null}
                  anchor="bottom start"
                  className="m-1 w-auto text-blue-600 hover:text-blue-800"
                  triggerIcon={<Icon name="PlusIcon" type="solid" size="sm" role="decorative" />}
                  actions={togglablePreviewTabs.map((tab) => ({
                    label: tab.label,
                    icon: visiblePreviewTabKeys.includes(tab.key) ? 'CheckIcon' : undefined,
                    onClick: () => visiblePreviewTabKeys.includes(tab.key)
                      ? removePreviewTab(tab.key)
                      : selectPreviewTab(tab.key, 'overflow_menu'),
                  }))}
                />
              }
            </div>
            <div className={"h-full overflow-y-scroll"} ref={scrollContainerRef}>
              {(activePreview === 'targetFields' && survey) &&
              <Suspense fallback={<SpacedSpinner />}>
                <TargetFieldExplorer survey={survey.survey} mockInfo={mockInfo} setMockInfo={setMockInfo} />
              </Suspense>}

              {(activePreview === 'notifications' && survey) &&
                <NotificationsView survey={survey} enableEmailMarkdownGlobally={survey.config?.experimental?.enableEmailMarkdownGlobally} />
              }

              {(activePreview === 'payments' && survey) &&
                <PaymentsEditor survey={survey} />
              }

              {(activePreview === 'amh' && survey) &&
                <AutomaticMessageHandler amh={survey.config?.comms?.automaticMessageHandler} />
              }

              {(activePreview === 'survey' && survey) &&
                <ScrollToTopContext.Provider value={{ scrollToTop, scrollContainerRef }}>
                  <SurveyPreview
                    survey={survey}
                    mockInfo={mockInfo}
                    setMockInfo={setMockInfo}
                    activeSurvey={activeSurvey}
                    setActiveSurvey={setActiveSurvey}
                    personas={survey.personas}
                    subsurveys={subsurveys}
                    state={stateRef.current!}
                  />
                </ScrollToTopContext.Provider>
              }

              {(activePreview === 'dashboards' && survey) &&
                <ConfigDashboardsPreview components={survey.survey} />
              }

              {(activePreview === 'robonav' && survey) &&
                <RoboNavConsole
                  onPersonaDetailsLoaded={setMockInfo}
                  surveys={enumerateSubsurveys(survey.survey)}
                  personas={survey.personas}
                />
              }

              {(activePreview === 'testCases' && survey) &&
                <TestCases survey={survey} />
              }

              {activePreview === 'changes' &&
                <Changes stateRef={stateRef} collabEnabled={collabEnabled} branchId={branchId} survey={survey} parentStateRef={parentStateRef} lastPublished={lastPublished} />}

              {(activePreview === 'fieldMismatches' && survey) &&
                <FieldMismatches fieldMismatches={fieldMismatches} survey={survey.survey} />
              }

              {(activePreview === 'validationSummary' && survey) &&
              <DistroValidation survey={survey} />}

              {(activePreview === 'navigation' && survey) &&
                <NavigationPreview expandedSurvey={survey} />
              }

              {(activePreview === 'applicantPortal' && survey) &&
                <ApplicantPortalPreview survey={survey} />
              }
            </div>
          </div>
          }
        </div>
      </div>
      <ResetProgramModal open={resetProgramModalOpen} onClose={() => setResetProgramModalOpen(false)} />
      <TranslationModal
        translations={translations}
        setTranslations={setTranslations}
        translationModalOpen={translationModalOpen}
        setTranslationModalOpen={setTranslationModalOpen}
        injestTranslations={injestTranslations}
        initialTranslations={initialTranslations}
        topLevelItems={getTopLevelItems(survey)}
        onScopeChange={handleTranslationScopeChange}
        surveyRoot={survey} />
      {survey && <ContentTemplateModal
        open={contentTemplateModalOpen}
        onClose={() => setContentTemplateModalOpen(false)}
        survey={survey}
        reloadSurvey={async () => await reloadSurvey(true)}
      />}
      <StartReviewConflictModal
        isOpen={startReviewConflictModalOpen}
        onClose={() => setStartReviewConflictModalOpen(false)}
        onConfirm={() => {
          setStartReviewConflictModalOpen(false);
          // Open the window immediately to avoid popup blocking
          // Note: Not using 'noopener,noreferrer' features as some browsers return null with that parameter
          const reviewWindow = window.open('/redirect', '_blank');
          startDistroReviewInternal(reviewWindow);
        }}
        reviewId={latestCommitMetadata?.reviewId}
      />
      {distroValidationErrorsForModal &&
        <DistroValidationModal
          results={distroValidationErrorsForModal}
          onClose={({openValidationPanel, publishAnyway}) => {
            if (openValidationPanel) {
              setShowRightPanel(true);
              selectPreviewTab('validationSummary', 'validation_link');
            }
            if (publishAnyway) {
              publish({skipDistroValidation: true});
            }
            setDistroValidationErrorsForModal(null);
          }}
        />
      }
      <PublishedChangesModal
        open={publishedChangesModalOpen}
        onClose={() => setPublishedChangesModalOpen(false)}
        fetchPublishedChanges={fetchPublishedChanges}
        branchId={branchId}
        parentStateRef={parentStateRef}
        parentOpsRef={parentOpsRef}
      />
      <ReviewRequiredModal
        isOpen={!!reviewRequired}
        onClose={() => setReviewRequired(null)}
        onProceedToReview={() => {
          setReviewRequired(null);
          // Open window immediately to avoid popup blocking
          const reviewWindow = window.open('/redirect', '_blank');
          startDistroReviewInternal(reviewWindow);
        }}
        reviewResult={reviewRequired}
      />
      <PublishProgressModal
        open={publishProgress.open}
        steps={collabEnabled
          ? (distroConfigName === SurveyName.ENTIRE_PROGRAM ? COLLAB_ENTIRE_PROGRAM_PUBLISH_STEPS : COLLAB_PUBLISH_STEPS)
          : (distroConfigName === SurveyName.ENTIRE_PROGRAM ? NON_COLLAB_ENTIRE_PROGRAM_PUBLISH_STEPS : NON_COLLAB_PUBLISH_STEPS)
        }
        currentStep={publishProgress.currentStep}
        done={publishProgress.done}
        error={publishProgress.error}
        isPublishing={publishing}
        onClose={publishProgress.onClose}
      />
      {showAIChat && <FloatingAIChat
        onClose={closeAIChat}
        onHeightChange={setAiChatHeight}
        survey={survey}
        // Read tools use this, not the survey prop: within one assistant response
        // several tool calls run before React re-renders, so an edit followed by a
        // read must still see the edit.
        getSurvey={() => surveyRef.current}
        onApplyTransaction={applyAIConfigTransaction}
        getValidationIssues={getAIValidationIssues}
        surveyName={distroConfigName}
        loadOtherSurvey={loadOtherSurvey}
        getSiblingBranchInfo={getSiblingBranchInfo}
        branchName={branchName}
        editSafety={editSafety}
        // Identity, not label: a branch name is not unique across configs, and
        // "Save as" swaps distroConfigName without any visible change.
        documentKey={`${distroConfigName}|${branchId ?? 'no-branch'}`}
        openRequest={aiChatOpenRequest}
      />}
    </UserInfoContext.Provider>

  </>
}

/**
 * Displays a list of instances in the Distro config where for a given piece of content, the target fields in one language
 * do not match those in another language.
 */
function FieldMismatches(props: { fieldMismatches: FieldMismatchMap, survey: Survey }): JSX.Element {

  if (Object.keys(props.fieldMismatches).length === 0) {
    return <div>No mismatches 🎉</div>
  }

  return (
    <div>
      {Object.entries(props.fieldMismatches).map(([mismatchKey, mismatch]) =>
        <div key={mismatchKey} className='p-2'>
          <button className='border-0 rounded-md bg-transparent text-blue-600 hover:text-blue-800 hover:bg-blue-300' onClick={() => findAndGoToComponent(mismatch.tree, props.survey)}>{mismatchKey}</button>
          {Object.keys(mismatch.fieldsInLangMap).map((langKey) =>
            <div key={mismatchKey + '_fm_' + langKey} className='flex'>
              {/* Languages and their fields */}
              <div className='px-2 border-r-2 mr-2'>
                <div>
                  <strong>{supportedLanguages[langKey as keyof Text]}</strong>
                  {mismatch.fieldsInLangMap[langKey as keyof Text].map((field, index) =>
                    <div key={mismatchKey + '_f_' + langKey + '_' + field + '_' + index}
                      className='pl-2'>
                      <span style={field === '[Empty]' ? { backgroundColor: 'moccasin', fontWeight: 'bold' } : {}}>{field}</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Languages and their content, with target fields highlighted */}
              <div>
                <br />
                {langKey !== '_id' && langKey !== 'kind' && highlightSearchTerm(
                  (mismatch.text as any)[langKey],
                  mismatch.fieldsInLangMap[langKey as keyof Text].map(field => field.slice(1)),
                  { wholeMatchOnly: true, matchCase: true, includeDashboards: true, includeComments: false, searchAllAttributes: false }
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
